import PropTypes from "prop-types";
import { Image as KonvaImage, Transformer } from "react-konva";
import { Html } from "react-konva-utils";
import React, { useEffect, useRef, useState } from "react";
import "../styles.css";
import KonvaButton from "../shape-editor/KonvaButton";
import { isPrimaryMouseButton } from "../../utils/pointerButtonUtils";
import { constrainOffCanvasPosition } from "../../utils/dragEndUtils";
import { getOffCanvasMainColumnBounds } from "../../utils/boundaryUtils";
const DEBUG_OCCLUSION = true;
const DEBUG_ROTATED_CROP_UNSELECT = false;
const rotatedImageCache = new Map();
const NON_SELECTED_IMAGE_CACHE_PIXEL_RATIO =
  typeof window !== "undefined"
    ? Math.min(1.35, window.devicePixelRatio || 1)
    : 1.35;
const TRANSFORMER_ANCHOR_BUFFER_PX = 10;
const DEBUG_IMAGE_TRANSFORMER_FLOW = true;

const getEffectiveInset = (requestedInset, stageSize, renderedSize) => {
  const safeInset = Math.max(0, Number(requestedInset) || 0);
  const safeStage = Math.max(0, Number(stageSize) || 0);
  const safeRendered = Math.max(0, Number(renderedSize) || 0);
  return Math.max(0, Math.min(safeInset, (safeStage - safeRendered) / 2));
};

const normalizeRotationDeg = (deg) => {
  let normalized = Number(deg) || 0;
  normalized %= 360;
  if (normalized < 0) normalized += 360;
  return normalized;
};

const isClientRectInsideStage = (
  rect,
  stageWidth,
  stageHeight,
  inset = 0,
  eps = 0.5,
) => {
  if (!rect) return true;
  const insetX = getEffectiveInset(inset, stageWidth, rect.width);
  const insetY = getEffectiveInset(inset, stageHeight, rect.height);
  return (
    rect.x >= insetX - eps &&
    rect.y >= insetY - eps &&
    rect.x + rect.width <= stageWidth - insetX + eps &&
    rect.y + rect.height <= stageHeight - insetY + eps
  );
};

const clampNodePositionInsideStageByClientRect = (
  node,
  stageWidth,
  stageHeight,
  inset = 0,
  options = {},
) => {
  if (!node) return { x: 0, y: 0 };
  const clampXEnabled = options.clampX !== false;
  const clampYEnabled = options.clampY !== false;
  const stage = node.getStage?.();
  // Multi-pass: getClientRect can shift slightly after each move with rotation/stroke.
  for (let pass = 0; pass < 8; pass += 1) {
    const rect = stage
      ? node.getClientRect({ relativeTo: stage })
      : node.getClientRect();
    const insetX = getEffectiveInset(inset, stageWidth, rect.width);
    const insetY = getEffectiveInset(inset, stageHeight, rect.height);
    let dx = 0;
    let dy = 0;

    if (clampXEnabled) {
      if (rect.x < insetX) dx = insetX - rect.x;
      else if (rect.x + rect.width > stageWidth - insetX) {
        dx = stageWidth - insetX - (rect.x + rect.width);
      }
    }

    if (clampYEnabled) {
      if (rect.y < insetY) dy = insetY - rect.y;
      else if (rect.y + rect.height > stageHeight - insetY) {
        dy = stageHeight - insetY - (rect.y + rect.height);
      }
    }

    if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) break;

    node.x((Number(node.x()) || 0) + dx);
    node.y((Number(node.y()) || 0) + dy);
    node.getLayer?.()?.batchDraw?.();
  }

  return { x: node.x(), y: node.y() };
};

const clampNodeInsideOffCanvasStripsByClientRect = (
  node,
  stageWidth,
  stageHeight,
  inset = 0,
  options = {},
) => {
  if (!node || stageWidth <= 0 || stageHeight <= 0) {
    return { x: node?.x?.() ?? 0, y: node?.y?.() ?? 0 };
  }
  const clampXEnabled = options.clampX !== false;
  const clampYEnabled = options.clampY !== false;

  const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
  const seamMidX = (mainZoneStart + mainZoneEnd) / 2;

  for (let pass = 0; pass < 8; pass += 1) {
    const rect = node.getClientRect();
    let dx = 0;
    let dy = 0;

    const insetX = getEffectiveInset(inset, stageWidth, rect.width);
    const insetY = getEffectiveInset(inset, stageHeight, rect.height);

    if (clampXEnabled) {
      if (rect.x < insetX) dx += insetX - rect.x;
      else if (rect.x + rect.width > stageWidth - insetX) {
        dx += stageWidth - insetX - (rect.x + rect.width);
      }
    }

    if (clampYEnabled) {
      if (rect.y < insetY) dy += insetY - rect.y;
      else if (rect.y + rect.height > stageHeight - insetY) {
        dy += stageHeight - insetY - (rect.y + rect.height);
      }
    }

    const overlapsMainColumn =
      rect.x < mainZoneEnd && rect.x + rect.width > mainZoneStart;
    if (overlapsMainColumn) {
      const rectCenterX = rect.x + rect.width / 2;
      if (rectCenterX >= seamMidX) {
        dx += mainZoneEnd - rect.x;
      } else {
        dx += mainZoneStart - (rect.x + rect.width);
      }
    }

    if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) break;

    node.x((Number(node.x()) || 0) + dx);
    node.y((Number(node.y()) || 0) + dy);
    node.getLayer?.()?.batchDraw?.();
  }

  return { x: node.x(), y: node.y() };
};

const isOffCanvasClientRectValid = (
  rect,
  stageWidth,
  stageHeight,
  inset = 0,
  eps = 0.5,
) => {
  if (!rect) return true;
  const insetX = getEffectiveInset(inset, stageWidth, rect.width);
  const insetY = getEffectiveInset(inset, stageHeight, rect.height);
  const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
  const insideStage =
    rect.x >= insetX - eps &&
    rect.y >= insetY - eps &&
    rect.x + rect.width <= stageWidth - insetX + eps &&
    rect.y + rect.height <= stageHeight - insetY + eps;
  const overlapsMainColumn =
    rect.x < mainZoneEnd - eps && rect.x + rect.width > mainZoneStart + eps;
  return insideStage && !overlapsMainColumn;
};

/** Avoid forcing Konva image() when parent passes a new Image() with the same src (causes flicker on unrelated re-renders). */
const bitmapDiffersFromNode = (nodeImage, nextImage) => {
  if (!nextImage) return false;
  if (nodeImage === nextImage) return false;
  const a = nodeImage?.src;
  const b = nextImage?.src;
  if (typeof a === "string" && typeof b === "string" && a === b && a.length > 0) {
    return false;
  }
  return true;
};

const ImageElement = ({
  img, // img contains id, x, y, width, height, rotation etc.
  onClick,
  onDragStart, // This is for group drag initialization
  onDragEnd, // This is for the image itself being dragged
  onDragMove, // This is for live updates during drag
  onTransformStart, // This is for when transform starts (resizing/rotating)
  onTransformEnd, // This is for the image itself being transformed (resized/rotated)
  onTransform, // 👈 Add new prop for live updates
  setImages,
  handleImageClick,
  isDraggable = true,
  effectiveZoomLevel = 1, // Zoom level for scaling elements
  constrainToStage = true,
  isOffCanvas = false,
  isPreview = false,
  isSelected = false,
  onCopyToOppositeCanvas,
  isTransforming = false, // Flag to disable interactions during transform
  isGroupDraggingRef, // Ref to track if we're in a group drag
  recentDraggableElementMousedownRef, // Ref to track mousedown for two-click selection behavior
  isCloneMode = false,
  lockedAction = null,
  isDuplicatePlacementActive = false,
  isMultiSelectMode = false,
  selectedObjectsCount = 0,
  containerRotationZero = false, // When true (e.g. preview), keep node at 0°; only crop/straighten content
  renderOcclusionBlocker = false, // Draw an alpha-aware blocker behind image to preserve occlusion while dimmed
  crossStageGestureBegin = null,
  crossStageGestureEnd = null,
}) => {
  // DEBUG: Log received props when cropData is present (for leave/revisit crop debugging)
  const hasCropData =
    img?.cropData &&
    (img.cropData.width > 0 ||
      img.cropData.left > 0 ||
      (img.cropData.left <= 1 && img.cropData.width <= 1));
  React.useEffect(() => {
    if (!hasCropData) return;
    const srcType =
      typeof img.src === "string"
        ? img.src.startsWith("data:")
          ? "data URL"
          : "http/other"
        : "n/a";
    const imageDataUrlType =
      typeof img.imageDataUrl === "string"
        ? img.imageDataUrl.startsWith("data:")
          ? "data URL"
          : "http/other"
        : "n/a";
  }, [
    hasCropData,
    img?.id,
    img?.cropData,
    img?.image,
    img?.originalImage,
    img?.src,
    img?.imageDataUrl,
    img?.originalWidth,
    img?.originalHeight,
    img?.isOnTopLayer,
  ]);

  const imageRef = useRef();
  const occlusionRef = useRef();
  const lastCacheModeRef = useRef(null); // "cached" | "uncached" | null
  const imageIsSelected =
    isSelected !== undefined ? isSelected : img.isSelected;
  const imageTransformerRef = useRef();
  const [isDragging, setIsDragging] = useState(false); // Track drag state for performance optimization
  const isDraggingRef = useRef(false); // Ref to track drag state synchronously (prevents prop changes during drag)
  const [rotatedOriginalImage, setRotatedOriginalImage] = useState(null); // Store pre-rotated image for crop
  const lastRotatedSourceKeyRef = useRef("");
  // Bump when image(s) get dimensions so crop recalculates on load (parent doesn't re-render on image onload).
  const [cropRecalcTick, setCropRecalcTick] = useState(0);
  const cropDataSignature = img?.cropData
    ? `${img.cropData.top ?? ""}|${img.cropData.left ?? ""}|${img.cropData.width ?? ""}|${img.cropData.height ?? ""}`
    : "no-crop";
  // CRITICAL FIX: Track if we just finished a transform to prevent React from overriding node dimensions
  const justFinishedTransformRef = useRef(false);
  // CRITICAL FIX: Track the last known Konva node position to detect group drag updates
  const lastKonvaPositionRef = useRef({ x: img.x, y: img.y });
  // CRITICAL FIX: For top-layer images, we need to read node position directly during render
  // to avoid stale props. We use a ref to cache the last known position and update it
  // in useLayoutEffect, then use it during render.
  const nodePositionRef = useRef({ x: img.x, y: img.y });
  // CRITICAL FIX: Track when drag just ended to prevent position restoration from overriding new position
  const justFinishedDragRef = useRef(false);
  const handledToggleOnMouseDownRef = useRef(false);
  // CRITICAL FIX: Store the position from drag end to use until state updates
  const dragEndPositionRef = useRef(null);
  // CRITICAL FIX: Ref to track the animation frame loop that forces position
  const positionOverrideAnimationRef = useRef(null);
  // CRITICAL FIX: Store top-left corner at transform start so we keep it fixed during resize (all images).
  // Shape: { topLeftX, topLeftY, useTopLeftAnchor } or null
  const transformStartPositionRef = useRef(null);
  const isMainNonTopTransformDebugEnabled = (() => {
    try {
      return (
        typeof window !== "undefined" &&
        window.localStorage?.getItem("debugMainNonTopImageTransform") === "1"
      );
    } catch {
      return false;
    }
  })();

  // When img has cropData, force re-render once image(s) have dimensions so crop recalculates.
  // Parent does not re-render on image onload; load event can fire before we attach. So we use
  // load listener + polling: poll until naturalWidth is available, then bump so useMemo runs again.
  useEffect(() => {
    if (!img.cropData || (!img.image && !img.originalImage)) return;
    const main = img.image;
    const orig = img.originalImage;
    const hasDims = () => {
      const w = main?.naturalWidth || orig?.naturalWidth;
      const h = main?.naturalHeight || orig?.naturalHeight;
      return typeof w === "number" && w > 0 && typeof h === "number" && h > 0;
    };
    const bump = () => {
      setCropRecalcTick((t) => t + 1);
    };
    if (hasDims()) {
      bump();
      return;
    }
    const onLoad = () => bump();
    if (main && typeof main.addEventListener === "function")
      main.addEventListener("load", onLoad);
    if (orig && orig !== main && typeof orig.addEventListener === "function")
      orig.addEventListener("load", onLoad);
    let count = 0;
    const id = setInterval(() => {
      count++;
      if (hasDims() || count > 40) {
        clearInterval(id);
        if (hasDims()) {
          bump();
        }
      }
    }, 50);
    return () => {
      clearInterval(id);
      if (main && typeof main.removeEventListener === "function")
        main.removeEventListener("load", onLoad);
      if (
        orig &&
        orig !== main &&
        typeof orig.removeEventListener === "function"
      )
        orig.removeEventListener("load", onLoad);
    };
  }, [img.id, cropDataSignature, img.image, img.originalImage]);

  // CRITICAL FIX: Use useLayoutEffect to restore node position AFTER React-Konva applies props
  // This runs synchronously before paint, so we can fix the position before the user sees it
  // CRITICAL FIX: Use useLayoutEffect to restore node position AFTER React-Konva applies props
  // This runs synchronously before paint, so we can fix the position before the user sees it

  // CRITICAL FIX: Track if we're dragging to off-canvas
  // This ref is set in onDragMove when we detect mouse is over off-canvas
  const isDraggingToOffCanvasRef = useRef(false);

  useEffect(() => {
    // Local transformer ownership:
    // - main canvas non-top-layer images => local transformer
    // - off-canvas top-layer images => local transformer
    // - off-canvas non-top-layer images => centralized offCanvasTransformerRef
    // - main canvas top-layer images => centralized topLayerTransformerRef
    const shouldAttachLocalTransformer =
      imageIsSelected &&
      ((isOffCanvas && img.isOnTopLayer) || (!isOffCanvas && !img.isOnTopLayer));

    if (DEBUG_IMAGE_TRANSFORMER_FLOW && imageIsSelected) {
    }

    // CRITICAL FIX: Don't attach transformer if dragging to off-canvas (for off-canvas top-layer images)
    if (
      shouldAttachLocalTransformer &&
      imageTransformerRef.current &&
      imageRef.current
    ) {
      // Check if we're dragging to off-canvas - if so, don't attach transformer
      if (img.isOnTopLayer && isOffCanvas && isDraggingToOffCanvasRef.current) {
        imageTransformerRef.current.nodes([]);
        imageTransformerRef.current.getLayer()?.batchDraw();
        return;
      }

      imageTransformerRef.current.nodes([imageRef.current]);
      // CRITICAL: Force recalculation when image geometry/source changes
      // (replace/crop, especially visible for off-canvas local transformer).
      if (typeof imageTransformerRef.current.forceUpdate === "function") {
        imageTransformerRef.current.forceUpdate();
      }
      imageTransformerRef.current.getLayer()?.batchDraw();
    }
  }, [
    imageIsSelected,
    img.isOnTopLayer,
    isOffCanvas,
    img.width,
    img.height,
    img.scaleX,
    img.scaleY,
    img.src,
    img._replacedAt,
  ]);

  // CRITICAL FIX: Update transformer anchors when maintainAspectRatio changes
  // Konva Transformer may not re-apply props during active drag,
  // so we directly update via setAttrs to ensure anchors stay visible.
  useEffect(() => {
    const tr = imageTransformerRef.current;
    if (!tr || !imageIsSelected) return;
    const anchors =
      img.maintainAspectRatio === true
        ? ["bottom-right"]
        : ["middle-right", "bottom-center", "bottom-right"];
    const effectiveAnchors = anchors; // Always show anchors, even during drag
    try {
      tr.setAttrs({
        enabledAnchors: effectiveAnchors,
        rotateEnabled: true, // Always enable rotation, even during drag
      });
      tr.getLayer()?.batchDraw();
    } catch (e) {
      // Ignore if transformer/node not ready
    }
  }, [isDragging, imageIsSelected, img.maintainAspectRatio]);

  // CRITICAL FIX: Clear transformer on unmount (when key changes due to replacement)
  useEffect(() => {
    return () => {
      if (imageTransformerRef.current) {
        imageTransformerRef.current.nodes([]);
      }
    };
  }, []);

  // PERFORMANCE: Cache image when not selected/transforming/dragging for faster redraws with many elements
  useEffect(() => {
    const node = imageRef.current;
    if (!node || isPreview) return;

    const shouldCache =
      !imageIsSelected &&
      !isTransforming &&
      !isDraggingRef.current &&
      // Cropped images may finalize their effective source/crop rectangle asynchronously
      // (especially API images with straighten + rotation). Caching too early can keep
      // an uncropped bitmap until next interaction; skip cache for cropped images.
      !img.cropData &&
      img.width > 0 &&
      img.height > 0;

    try {
      if (shouldCache) {
        if (lastCacheModeRef.current === "cached") return;
        if (typeof node.cache === "function") {
          node.cache({ pixelRatio: NON_SELECTED_IMAGE_CACHE_PIXEL_RATIO });
          node.getLayer()?.batchDraw();
          lastCacheModeRef.current = "cached";
        }
      } else {
        if (lastCacheModeRef.current === "uncached") return;
        if (typeof node.clearCache === "function") {
          node.clearCache();
          node.getLayer()?.batchDraw();
          lastCacheModeRef.current = "uncached";
        }
      }
    } catch (e) {
      // Ignore cache errors (e.g. node not fully ready)
    }
  }, [
    imageIsSelected,
    isTransforming,
    isDragging,
    img.id,
    img.width,
    img.height,
    img.src,
    img.cropData,
    isPreview,
  ]);

  // Reset cache-mode tracker when element identity changes.
  useEffect(() => {
    lastCacheModeRef.current = null;
  }, [img.id]);

  // CRITICAL FIX: Continuously lock node position ONLY when dragging to off-canvas
  // This runs every frame to ensure the node doesn't move when dragging to off-canvas
  // But allows normal drag when dragging on main canvas
  React.useLayoutEffect(() => {
    if (
      img.isOnTopLayer &&
      !isOffCanvas &&
      !isPreview &&
      isDragging &&
      nodePositionRef.current &&
      imageRef.current
    ) {
      const node = imageRef.current;
      const nodeIsDragging = node.getAttr("_isDragging") === true;

      // Don't lock position when dragging to off-canvas - allow it to change
      // The original image is hidden via opacity: 0 in Canvas.jsx, so it can move freely
    }
  });

  React.useLayoutEffect(() => {
    const node = imageRef.current;
    if (!node) return;

    // CRITICAL: During transform with useTopLeftAnchor, override position after React-Konva applies props.
    // Use transformStartPositionRef (set at transform start) - don't rely on isTransforming prop which may not
    // trigger re-renders since it's ref-based.
    const ref = transformStartPositionRef.current;
    if (ref?.useTopLeftAnchor) {
      const w = node.width() * (node.scaleX() || 1);
      const h = node.height() * (node.scaleY() || 1);
      node.x(ref.topLeftX);
      node.y(ref.topLeftY);
      node.getLayer()?.batchDraw();
      return;
    }

    // CRITICAL FIX: Don't fix position/offsets during transform - let transformer handle it
    if (justFinishedTransformRef.current) {
      return; // Skip position/offset fixes during transform
    }

    // CRITICAL FIX: Match RectangleElement's approach - only fix offsets for useTopLeftAnchor
    // Don't try to fix position from props, as that can cause reverts after drag end
    // React-Konva will apply the correct props once state updates
    if (img.useTopLeftAnchor === true) {
      // CRITICAL FIX: For top-layer images being dragged to off-canvas, don't update position
      // The original image should stay in place, only the drag preview should move
      if (
        img.isOnTopLayer &&
        !isOffCanvas &&
        isDraggingToOffCanvasRef.current
      ) {
        return; // Skip position updates when dragging to off-canvas
      }

      // CRITICAL: For images with useTopLeftAnchor, offsetX/offsetY MUST be 0
      // Only fix offsets, not position - let React-Konva handle position from props
      const currentOffsetX = node.offsetX();
      const currentOffsetY = node.offsetY();

      if (currentOffsetX !== 0 || currentOffsetY !== 0) {
        node.offsetX(0);
        node.offsetY(0);
        node.getLayer()?.batchDraw();
      }
    }
  }, [
    img.id,
    img.useTopLeftAnchor,
    img.width,
    img.height,
    img.isOnTopLayer,
    isDragging,
  ]);

  // REMOVED: Complex position-fixing logic that was causing reverts
  // RectangleElement doesn't have this, and it works correctly
  // React-Konva will apply the correct position from props once state updates

  // REMOVED: Position-fixing effect for off-canvas images
  // RectangleElement doesn't have this, and it works correctly
  // React-Konva will apply the correct position from props once state updates

  // CRITICAL FIX: Update lastKonvaPositionRef when props change after group drag sync
  // This ensures we track the correct position and don't revert after state updates
  React.useEffect(() => {
    if (img.isOnTopLayer && imageRef.current) {
      const nodeX = imageRef.current.x();
      const nodeY = imageRef.current.y();
      const positionDiff = Math.abs(nodeX - img.x) + Math.abs(nodeY - img.y);

      if (positionDiff < 0.1) {
        // Node and props match - state has been synced, update lastKonvaPositionRef
        lastKonvaPositionRef.current = { x: nodeX, y: nodeY };
      } else if (positionDiff > 1) {
        // Node and props don't match - node is more recent (from group drag)
        lastKonvaPositionRef.current = { x: nodeX, y: nodeY };
      }
    }
  }, [img.x, img.y, img.isOnTopLayer]);

  // CRITICAL FIX: During group drag, prevent React from overriding Konva node position
  // handleGroupDragMove updates the Konva node directly, and we need to preserve that
  // This effect runs on every render to ensure the node position isn't reset by React-Konva
  useEffect(() => {
    if (!img.isOnTopLayer || !imageRef.current) return;

    const node = imageRef.current;

    // Only maintain position during active group drag
    if (isGroupDraggingRef?.current) {
      // Store the current node position (which was set by handleGroupDragMove)
      const currentNodeX = node.x();
      const currentNodeY = node.y();

      // If React-Konva tries to apply props that differ from the node position,
      // restore the node position (which is the correct one from handleGroupDragMove)
      // This prevents the fluctuation caused by React trying to apply stale props
      if (
        Math.abs(currentNodeX - img.x) > 0.1 ||
        Math.abs(currentNodeY - img.y) > 0.1
      ) {
        // Node position differs from props - keep the node position (it's more current)
        node.x(currentNodeX);
        node.y(currentNodeY);
      }
    }
  }, [img.x, img.y, img.isOnTopLayer, isGroupDraggingRef]);

  // CRITICAL FIX: Prevent React from overriding node dimensions immediately after transform
  // If we just finished a transform, the node already has the correct dimensions
  // Don't let React props override them until state has updated
  useEffect(() => {
    if (justFinishedTransformRef.current && imageRef.current) {
      const node = imageRef.current;
      const nodeWidth = node.width();
      const nodeHeight = node.height();

      // If node dimensions don't match props, but we just finished transform,
      // keep the node dimensions (they're correct from onTransformEnd)
      // Only update if props match node (meaning state has updated)
      if (nodeWidth !== img.width || nodeHeight !== img.height) {
        // Keep node dimensions, don't update from props
        node.width(nodeWidth);
        node.height(nodeHeight);
        node.getLayer()?.batchDraw();
      } else {
        // Props match node, state has updated, clear the flag
        justFinishedTransformRef.current = false;
      }
    }
  }, [img.width, img.height]);

  // CRITICAL FIX: One-time correction for database scale inconsistencies
  // API images with flips should have negative scale, but DB might have wrong values
  useEffect(() => {
    const isApiImage =
      img.src &&
      (img.src.startsWith("http://") || img.src.startsWith("https://"));
    const hasBaked = img.hasBakedTransforms ?? false;
    const shouldApplyFlipViaScale = isApiImage || !hasBaked;

    if (!shouldApplyFlipViaScale) return; // Baked local images don't need correction

    const flipH = img.flipHorizontal ?? false;
    const flipV = img.flipVertical ?? false;
    const currentScaleX = img.scaleX ?? 1;
    const currentScaleY = img.scaleY ?? 1;

    // Calculate expected scale signs
    const expectedScaleX = flipH
      ? -Math.abs(currentScaleX)
      : Math.abs(currentScaleX);
    const expectedScaleY = flipV
      ? -Math.abs(currentScaleY)
      : Math.abs(currentScaleY);

    // Check if scale needs correction
    const needsScaleXCorrection = currentScaleX !== expectedScaleX;
    const needsScaleYCorrection = currentScaleY !== expectedScaleY;

    if (needsScaleXCorrection || needsScaleYCorrection) {
      // Update image properties with corrected scale
      setImages?.((prevImages) =>
        prevImages.map((image) =>
          image.id === img.id
            ? {
                ...image,
                scaleX: expectedScaleX,
                scaleY: expectedScaleY,
              }
            : image,
        ),
      );
    }
  }, []); // Run only once on mount

  // CRITICAL FALLBACK: Self-bake API images on first render if transforms exist
  // This catches cases where IndexedDB cache bypassed MyCanvas baking
  useEffect(() => {
    // CRITICAL FIX: Skip self-bake if image was just replaced
    // The replacement already provides a fresh, correctly formatted image
    if (img._replacedAt) {
      return;
    }

    const isApiImage =
      img.src &&
      (img.src.startsWith("http://") || img.src.startsWith("https://"));
    // Only bake when flips are present. For rotation-only, let Konva handle it
    // to avoid temporary double-rotation and bounding-box expansion.
    const hasTransforms = img.flipHorizontal || img.flipVertical;

    // If already baked (data URL) or no transforms, skip
    if (!isApiImage || !hasTransforms) return;

    // CRITICAL FIX: Skip self-bake if image has crop data and hasBakedTransforms
    // This means the crop operation already baked the transforms into the cropped image
    // Running self-bake would overwrite the cropped image with the full image
    if (img.cropData && img.hasBakedTransforms) {
      return;
    }

    // Need a valid HTMLImageElement to bake
    const source = imageSource;
    if (!source || !(source instanceof window.Image)) return;

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const flipH = img.flipHorizontal || false;
      const flipV = img.flipVertical || false;

      // Prefer pre-rotated source when available so we can crop directly
      const hasRotation = (img.originalCropRotation || 0) !== 0;
      const bakeSource =
        rotatedOriginalImage && hasRotation ? rotatedOriginalImage : source;

      const w = bakeSource.naturalWidth || bakeSource.width;
      const h = bakeSource.naturalHeight || bakeSource.height;
      if (!w || !h) return;

      const hasCrop =
        shouldApplyCrop &&
        cropProperties &&
        cropProperties.cropWidth > 0 &&
        cropProperties.cropHeight > 0;

      if (hasCrop) {
        const { cropX, cropY, cropWidth, cropHeight } = cropProperties;
        // Canvas equals cropped region
        // Use Math.ceil to ensure we capture all content (cropWidth/cropHeight are already precise)
        // This prevents any content from being cut off due to rounding
        canvas.width = Math.max(1, Math.ceil(cropWidth));
        canvas.height = Math.max(1, Math.ceil(cropHeight));

        ctx.save();
        // Apply flips around crop center if needed
        if (flipH || flipV) {
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
          ctx.drawImage(
            bakeSource,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            -canvas.width / 2,
            -canvas.height / 2,
            canvas.width,
            canvas.height,
          );
        } else {
          // No flips, draw directly
          ctx.drawImage(
            bakeSource,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            canvas.width,
            canvas.height,
          );
        }
        ctx.restore();
      } else {
        // No crop: bake entire image, including rotation for non-rotated source
        const rotation =
          hasRotation && bakeSource === source
            ? (img.originalCropRotation || 0) * (Math.PI / 180)
            : 0;
        const cos = Math.abs(Math.cos(rotation));
        const sin = Math.abs(Math.sin(rotation));
        const rotatedW = rotation === 0 ? w : w * cos + h * sin;
        const rotatedH = rotation === 0 ? h : w * sin + h * cos;

        canvas.width = Math.max(1, Math.round(rotatedW));
        canvas.height = Math.max(1, Math.round(rotatedH));

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
        if (rotation !== 0) ctx.rotate(rotation);
        ctx.drawImage(bakeSource, -w / 2, -h / 2, w, h);
        ctx.restore();
      }

      const bakedUrl = canvas.toDataURL("image/png");
      const bakedImg = new window.Image();
      bakedImg.onload = () => {
        setImages?.((prev) =>
          prev.map((it) =>
            it.id === img.id
              ? {
                  ...it,
                  src: bakedUrl,
                  image: bakedImg,
                  originalImage: source,
                  originalImageDataUrl: it.src,
                  hasBakedTransforms: true,
                  scaleX: 1,
                  scaleY: 1,
                }
              : it,
          ),
        );
      };
      bakedImg.src = bakedUrl;
    } catch (e) {
      // Self-bake failed
    }
    // Only run once per image id and src
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [img.id]);

  // Effect to create a pre-rotated version of the original image when originalCropRotation exists
  // This applies the straightening BEFORE crop and flip transformations
  // PERFORMANCE FIX: Only create rotated image when actually needed (has crop data)
  useEffect(() => {
    // Check if this is an API image (S3 URL)
    const isApiImage =
      img.src &&
      (img.src.startsWith("http://") || img.src.startsWith("https://"));

    // PERFORMANCE FIX: Skip if no crop data - rotated image is only needed for cropping
    if (!img.cropData) {
      lastRotatedSourceKeyRef.current = "";
      setRotatedOriginalImage(null);
      return;
    }

    // Pre-rotate to get correct dimensions for crop calculation
    // We create rotated image just for dimension reference, not for actual display
    // Skip only if transforms are already baked for local blobs

    if (
      !img.originalImage ||
      img.originalCropRotation === undefined ||
      img.originalCropRotation === 0 ||
      (img.hasBakedTransforms && !isApiImage) // Skip if baked (except for API images)
    ) {
      lastRotatedSourceKeyRef.current = "";
      setRotatedOriginalImage(null);
      return;
    }

    const sourceKey = [
      img.id,
      img.src || "",
      img.originalImage?.src || "",
      img.originalCropRotation || 0,
      img.flipHorizontal ? 1 : 0,
      img.flipVertical ? 1 : 0,
      cropDataSignature,
      img.hasBakedTransforms ? 1 : 0,
    ].join("|");

    // Prevent redundant re-rotation when only object references changed.
    if (lastRotatedSourceKeyRef.current === sourceKey) {
      return;
    }
    lastRotatedSourceKeyRef.current = sourceKey;

    // Reuse cached pre-rotated image across remounts (prevents original->rotated flip flicker).
    const cachedRotated = rotatedImageCache.get(sourceKey);
    if (cachedRotated instanceof window.Image) {
      setRotatedOriginalImage(cachedRotated);
      return;
    }

    const createRotatedImage = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const originalImg = img.originalImage;
      let rotationDegrees = img.originalCropRotation || 0;

      // For API images, apply transforms in correct order: flip → rotate → crop
      // We need to apply flips BEFORE rotation when creating the rotated image
      const rotationRadians = (rotationDegrees * Math.PI) / 180;

      // Calculate rotated canvas dimensions
      const cos = Math.abs(Math.cos(rotationRadians));
      const sin = Math.abs(Math.sin(rotationRadians));
      const rotatedWidth =
        originalImg.naturalWidth * cos + originalImg.naturalHeight * sin;
      const rotatedHeight =
        originalImg.naturalWidth * sin + originalImg.naturalHeight * cos;

      canvas.width = rotatedWidth;
      canvas.height = rotatedHeight;

      // CRITICAL: For API images, create rotatedOriginalImage WITHOUT flips
      // Flips will be applied via Konva scale to maintain correct transform order
      // This ensures: crop (on rotated image) → flip (via Konva scale) → display
      ctx.translate(rotatedWidth / 2, rotatedHeight / 2);

      // CRITICAL FIX: Apply rotation inversion only for specific flip cases
      // The rotation inversion is needed because:
      // 1. API images with flip vertical only: rotation appears inverted
      // 2. API images with flip horizontal only: rotation appears inverted
      // 3. API images with no flips or both flips: use normal rotation
      // This targeted approach prevents issues with complex flip combinations
      const shouldInvertRotation =
        isApiImage &&
        ((img.flipVertical && !img.flipHorizontal) ||
          (img.flipHorizontal && !img.flipVertical));
      const finalRotationRadians = shouldInvertRotation
        ? -rotationRadians
        : rotationRadians;

      ctx.rotate(finalRotationRadians);

      ctx.drawImage(
        originalImg,
        -originalImg.naturalWidth / 2,
        -originalImg.naturalHeight / 2,
        originalImg.naturalWidth,
        originalImg.naturalHeight,
      );

      // Create new image from rotated canvas
      const rotatedImg = new window.Image();
      rotatedImg.onload = () => {
        rotatedImageCache.set(sourceKey, rotatedImg);
        setRotatedOriginalImage((prev) =>
          prev?.src === rotatedImg.src ? prev : rotatedImg,
        );
      };
      rotatedImg.src = canvas.toDataURL("image/png");
    };

    createRotatedImage();
  }, [
    cropDataSignature,
    img.originalImage,
    img.originalCropRotation,
    img.hasBakedTransforms,
    img.src,
    img.id,
    img.flipVertical, // Re-create when flip changes (affects rotation direction)
    img.flipHorizontal, // Re-create when flip changes (affects rotation direction)
  ]);

  // Calculate crop properties for Konva Image
  const getCropProperties = () => {
    if (!img.cropData) return {};
    const { top, left, width, height } = img.cropData;

    // Choose the correct dimension basis for crop calculations
    // For API images with pre-rotation available, use rotated image dimensions
    // Otherwise use the original image dimensions (from loaded image or saved originalWidth/originalHeight for re-entry)
    const isApiImgForCrop =
      img.src &&
      (img.src.startsWith("http://") || img.src.startsWith("https://"));
    const hasPreRotation =
      !!rotatedOriginalImage &&
      !!img.originalCropRotation &&
      img.originalCropRotation !== 0;
    let imageWidth =
      isApiImgForCrop && hasPreRotation
        ? rotatedOriginalImage.naturalWidth || rotatedOriginalImage.width
        : img.originalImage?.naturalWidth ||
          img.image?.naturalWidth ||
          img.originalWidth ||
          0;
    let imageHeight =
      isApiImgForCrop && hasPreRotation
        ? rotatedOriginalImage.naturalHeight || rotatedOriginalImage.height
        : img.originalImage?.naturalHeight ||
          img.image?.naturalHeight ||
          img.originalHeight ||
          0;
    if (!imageWidth || !imageHeight) {
      return {};
    }

    // Convert normalized coordinates to pixel coordinates (0-1 range from cropper)
    const isNormalized = left <= 1 && top <= 1 && width <= 1 && height <= 1;

    let cropX, cropY, cropWidth, cropHeight;

    if (isNormalized) {
      // CRITICAL FIX: Use floor for start positions and ceil for end positions
      // to ensure we capture the full crop area without cutting off content
      // This prevents precision loss from rounding that causes content to be cut
      const startX = left * imageWidth;
      const startY = top * imageHeight;
      const endX = (left + width) * imageWidth;
      const endY = (top + height) * imageHeight;

      // Use floor for start to ensure we don't go outside bounds
      cropX = Math.max(0, Math.floor(startX));
      cropY = Math.max(0, Math.floor(startY));

      // Use ceil for end positions to ensure we capture all content, then calculate width/height
      const cropEndX = Math.min(imageWidth, Math.ceil(endX));
      const cropEndY = Math.min(imageHeight, Math.ceil(endY));

      // Calculate width and height from the precise bounds
      cropWidth = Math.max(1, cropEndX - cropX);
      cropHeight = Math.max(1, cropEndY - cropY);
    } else {
      // Already in pixel coordinates
      cropX = left;
      cropY = top;
      cropWidth = width;
      cropHeight = height;
    }

    // If crop is in pixels and we are NOT using a pre-rotated basis, map from rotated canvas
    // back to original image coordinates to align with Konva's crop-then-rotate order
    if (
      !isNormalized &&
      img.originalCropRotation &&
      !(isApiImgForCrop && hasPreRotation)
    ) {
      const theta = (img.originalCropRotation * Math.PI) / 180;
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);

      // Rotated canvas dimensions that enclose the rotated original image
      const rotatedW = Math.abs(imageWidth * cos) + Math.abs(imageHeight * sin);
      const rotatedH = Math.abs(imageWidth * sin) + Math.abs(imageHeight * cos);

      const toOriginal = (xr, yr) => {
        const cxr = xr - rotatedW / 2;
        const cyr = yr - rotatedH / 2;
        const xo = cxr * cos + cyr * sin; // rotate by -theta
        const yo = -cxr * sin + cyr * cos;
        return { x: xo + imageWidth / 2, y: yo + imageHeight / 2 };
      };

      const corners = [
        toOriginal(cropX, cropY),
        toOriginal(cropX + cropWidth, cropY),
        toOriginal(cropX, cropY + cropHeight),
        toOriginal(cropX + cropWidth, cropY + cropHeight),
      ];

      const minX = Math.max(0, Math.min(...corners.map((c) => c.x)));
      const minY = Math.max(0, Math.min(...corners.map((c) => c.y)));
      const maxX = Math.min(imageWidth, Math.max(...corners.map((c) => c.x)));
      const maxY = Math.min(imageHeight, Math.max(...corners.map((c) => c.y)));

      const mappedW = Math.max(1, Math.round(maxX - minX));
      const mappedH = Math.max(1, Math.round(maxY - minY));

      cropX = Math.round(minX);
      cropY = Math.round(minY);
      cropWidth = mappedW;
      cropHeight = mappedH;
    }

    // Map crop from flipped-view coordinates back to original image space
    // Konva applies crop before transforms. If the user cropped after applying
    // a visual flip via scale (API images or non-baked), we must mirror the
    // crop rectangle so it selects the intended region in the original space.
    // When using pre-rotated basis for API images, we still mirror for flips but
    // on the rotated dimension basis selected above.
    {
      const isApiImageLocal =
        img.src &&
        (img.src.startsWith("http://") || img.src.startsWith("https://"));
      const shouldApplyFlipViaScaleLocal =
        isApiImageLocal || !img.hasBakedTransforms;

      if (shouldApplyFlipViaScaleLocal) {
        if (img.flipHorizontal) {
          cropX = Math.max(
            0,
            Math.min(
              imageWidth - Math.max(1, cropWidth),
              imageWidth - cropX - cropWidth,
            ),
          );
        }
        if (img.flipVertical) {
          cropY = Math.max(
            0,
            Math.min(
              imageHeight - Math.max(1, cropHeight),
              imageHeight - cropY - cropHeight,
            ),
          );
        }
      }
    }

    // Validate crop coordinates
    if (cropX < 0 || cropY < 0 || cropWidth <= 0 || cropHeight <= 0) {
      return {};
    }

    // Check if crop coordinates are within image bounds
    if (cropX >= imageWidth || cropY >= imageHeight) {
      return {};
    }

    // Final validation
    if (
      cropX < 0 ||
      cropY < 0 ||
      cropWidth <= 0 ||
      cropHeight <= 0 ||
      cropX >= imageWidth ||
      cropY >= imageHeight
    ) {
      return {};
    }

    // Clamp so crop never exceeds image bounds (prevents artifact/little image and white area in transformer)
    const safeCropX = Math.max(0, Math.min(cropX, imageWidth - 1));
    const safeCropY = Math.max(0, Math.min(cropY, imageHeight - 1));
    const safeCropWidth = Math.max(
      1,
      Math.min(cropWidth, imageWidth - safeCropX),
    );
    const safeCropHeight = Math.max(
      1,
      Math.min(cropHeight, imageHeight - safeCropY),
    );
    const cropProps = {
      cropX: safeCropX,
      cropY: safeCropY,
      cropWidth: safeCropWidth,
      cropHeight: safeCropHeight,
    };
    return cropProps;
  };

  // ⚡ PERFORMANCE: useMemo to prevent recalculating crop properties on every render
  // CRITICAL FIX: Add more dependencies to prevent stale calculations
  const cropProperties = React.useMemo(
    () => getCropProperties(),
    [
      cropRecalcTick, // Re-run when image(s) get dimensions (load/poll in effect bumps this)
      cropDataSignature,
      img.image,
      img.originalImage,
      rotatedOriginalImage,
      img.originalCropRotation,
      img.id,
    ],
  );

  // SIMPLIFIED APPROACH: Use crop properties when appropriate
  // For API images with complex transforms, we need to apply crop to rotatedOriginalImage
  // The cropData coordinates are measured on the rotated image, so they should work with rotatedOriginalImage
  const isApiImage =
    img.src &&
    (img.src.startsWith("http://") || img.src.startsWith("https://"));
  const hasComplexTransforms =
    img.hasBakedTransforms &&
    (img.flipHorizontal ||
      img.flipVertical ||
      (img.originalCropRotation && img.originalCropRotation !== 0));
  const hasStraightenedCrop =
    !!img.cropData &&
    img.originalCropRotation !== undefined &&
    img.originalCropRotation !== 0;
  const debugPrevSignatureRef = useRef(null);

  // When img.src is updated (e.g. after replace) but img.image is still stale (e.g. old blob),
  // load img.src into a new Image so we can display the new image (fixes draw-to-create replace).
  // Must be declared before imageSource useMemo which uses it.
  const [syncedImageFromSrc, setSyncedImageFromSrc] = useState(null);
  useEffect(() => {
    if (!img.src || typeof img.src !== "string") {
      setSyncedImageFromSrc(null);
      return;
    }
    const imageInSync = img.image && img.image.src === img.src;
    if (imageInSync) {
      setSyncedImageFromSrc(null);
      return;
    }
    const imgEl = new Image();
    let cancelled = false;
    imgEl.onload = () => {
      if (!cancelled) setSyncedImageFromSrc(imgEl);
    };
    imgEl.onerror = () => {
      if (!cancelled) setSyncedImageFromSrc(null);
    };
    imgEl.src = img.src;
    return () => {
      cancelled = true;
      setSyncedImageFromSrc(null);
    };
  }, [img.id, img.src]);

  // ⚡ PERFORMANCE: useMemo to prevent recalculating image source on every render
  // When `compress` toggle is ON, prefer the compressed/preview bitmap to lower memory and draw cost.
  const imageSource = React.useMemo(() => {
    // STABILITY FIX: For rotated+cropped images, pin to pre-rotated source whenever available.
    // This prevents selection-driven branch flips between rotated/non-rotated sources.
    if (hasStraightenedCrop && rotatedOriginalImage) {
      return rotatedOriginalImage;
    }

    // If compression toggle is enabled and we have a compressed preview, always use that.
    if (img.compress && img.compressedImage) {
      return img.compressedImage;
    }

    // CRITICAL: When src is the cropped imageDataUrl, always use img.image (the cropped bitmap).
    // Prevents wrong crop region after redo / leave-re-enter (e.g. applying cropData to original or to syncedImageFromSrc).
    // For API images, use cropped bitmap when imageDataUrl is a data URL (saved on leave).
    const isCroppedDataUrl =
      img.imageDataUrl &&
      typeof img.imageDataUrl === "string" &&
      img.imageDataUrl.startsWith("data:");
    if (
      img.imageDataUrl &&
      img.src === img.imageDataUrl &&
      img.image &&
      (!isApiImage || isCroppedDataUrl)
    ) {
      return img.image;
    }
    // CRITICAL: For cropped images (no baked transforms), always use img.image when it's the cropped result.
    // This must run before syncedImageFromSrc so that after unselect we don't use an image loaded from
    // img.src and then re-apply crop (which can show white/blank for non-top-layer cropped images).
    if (img.cropData && !img.hasBakedTransforms && img.image && !isApiImage) {
      if (img.image !== img.originalImage) {
        return img.image; // Already the cropped result; don't use syncedImageFromSrc
      }
    }

    // When img.image is stale (e.g. after replace, parent passed new img.src but old img.image),
    // use the image we loaded from img.src so the new image displays (draw-to-create and others).
    if (syncedImageFromSrc && img.src && syncedImageFromSrc.src === img.src) {
      return syncedImageFromSrc;
    }
    // CRITICAL FIX: For replaced images, always use img.image directly
    // Don't use originalImage or any cached versions.
    // Also use img.image when image.src matches img.src (replacement just applied; _replacedAt may be set in a later tick).
    // EXCEPTION: When we have API + cropData + originalCropRotation, we must use rotatedOriginalImage so crop is in rotated space (fixes bottom cut in MyCanvas).
    const isReplaced =
      img._replacedAt || (img.image && img.src && img.image.src === img.src);
    const needsRotatedForCrop =
      isApiImage &&
      img.cropData &&
      img.originalCropRotation !== undefined &&
      img.originalCropRotation !== 0 &&
      rotatedOriginalImage;
    if (isReplaced && img.image && !needsRotatedForCrop) {
      return img.image;
    }
    if (needsRotatedForCrop) {
      if (img.cropData) return rotatedOriginalImage;
    }

    // CRITICAL FIX: For top-layer images with crop data, check if we need to use originalImage for cropping
    // On first crop, img.image might still be the uncropped original, so we need to use originalImage
    // to apply the crop. After the crop is applied and state updates, img.image will be the cropped version.
    if (img.isOnTopLayer && img.cropData && img.originalImage) {
      // Check if img.image dimensions match the expected cropped dimensions
      // If they don't match, we're in the middle of a crop operation and should use originalImage
      const imageNaturalWidth = img.image?.naturalWidth || img.image?.width;
      const imageNaturalHeight = img.image?.naturalHeight || img.image?.height;
      const originalNaturalWidth =
        img.originalImage?.naturalWidth || img.originalImage?.width;
      const originalNaturalHeight =
        img.originalImage?.naturalHeight || img.originalImage?.height;

      // If img.image has the same dimensions as originalImage, it means the crop hasn't been applied yet
      // Use originalImage so the crop can be applied via cropProperties.
      // Require positive dimensions so we don't wrongly use originalImage when both report 0 (AVIF/async load).
      if (
        imageNaturalWidth === originalNaturalWidth &&
        imageNaturalHeight === originalNaturalHeight &&
        imageNaturalWidth > 0 &&
        imageNaturalHeight > 0
      ) {
        // For images with rotation, use rotatedOriginalImage if available
        if (
          rotatedOriginalImage &&
          img.originalCropRotation !== undefined &&
          img.originalCropRotation !== 0
        ) {
          if (img.cropData) return rotatedOriginalImage;
        }
        
        return img.originalImage;
      }
    }

    // CRITICAL FIX: If image has BAKED transforms AND crop data, use the loaded image (img.image)
    // BUT only for LOCAL blobs where transforms are actually baked into the pixels
    // For API images, img.image is just the original S3 image, not transformed
    // EXCEPTION: If img.image is the same as img.originalImage (same object reference),
    // it means the crop hasn't been applied yet, so use originalImage for cropping
    if (
      img.hasBakedTransforms &&
      img.cropData &&
      img.image &&
      (img.flipHorizontal || img.flipVertical || img.originalCropRotation) &&
      !isApiImage // Only use baked image for local blobs, not API images
    ) {
      // Check if img.image is the same object as originalImage (crop not applied yet)
      if (img.image === img.originalImage) {
        // Crop not applied yet, use originalImage so crop can be applied
        if (
          rotatedOriginalImage &&
          img.originalCropRotation !== undefined &&
          img.originalCropRotation !== 0
        ) {
          return rotatedOriginalImage;
        }
        return img.originalImage;
      }
      // img.image is different from originalImage, so it's the cropped version
      return img.image; // Use the loaded image without re-applying transforms
    }

    // Cropped without baked transforms (continued): when img.image === img.originalImage, crop not applied yet
    if (img.cropData && !img.hasBakedTransforms && img.image && !isApiImage) {
      if (img.image === img.originalImage) {
        if (
          rotatedOriginalImage &&
          img.originalCropRotation !== undefined &&
          img.originalCropRotation !== 0
        ) {
          return rotatedOriginalImage;
        }
        return img.originalImage;
      }
    }

    // If we have crop data and transforms are not baked OR it's an API image,
    // use the most appropriate source for cropping:
    // - For API images with rotation, prefer the pre-rotated original image
    // - Otherwise fall back to the unrotated original image or regular image
    if (img.cropData && (!img.hasBakedTransforms || isApiImage)) {
      if (
        isApiImage &&
        rotatedOriginalImage &&
        img.originalCropRotation !== undefined &&
        img.originalCropRotation !== 0
      ) {
        return rotatedOriginalImage;
      }
      // Use _originalImage if available (for cropping operations that need full resolution)
      if (img.originalImage || img._originalImage) {
        return img.originalImage || img._originalImage;
      }
    }

    // DOWNSCALING DISABLED: Skip _downscaledImage so advanced crop modal gets full resolution
    // if (img._downscaledImage && img._downscaledImage instanceof window.Image) {
    //   if (img.cropData) logBranch("_downscaledImage", "");
    //   return img._downscaledImage;
    // }

    // Use the current image (full resolution when downscaling is disabled)
    return img.image;
  }, [
    cropRecalcTick,
    hasStraightenedCrop,
    cropDataSignature,
    img.image,
    img.imageDataUrl,
    img.originalImage,
    img._originalImage,
    img._downscaledImage,
    rotatedOriginalImage,
    img.originalCropRotation,
    img.flipHorizontal,
    img.flipVertical,
    img.hasBakedTransforms,
    img.id,
    img.isOnTopLayer,
    isApiImage,
    img.src,
    img._replacedAt,
    syncedImageFromSrc,
  ]);

  // Determine if we should apply crop based on the selected image source
  const shouldApplyCrop = React.useMemo(() => {
    const hasCropData =
      cropProperties && Object.keys(cropProperties).length > 0;

    // Compute once for use in "already cropped" and "img.image is cropped result" checks
    let dimensionsMatchCrop = false;
    if (img.cropData && imageSource) {
      const cw = img.cropData.width;
      const ch = img.cropData.height;
      const iw = imageSource?.naturalWidth || imageSource?.width || 0;
      const ih = imageSource?.naturalHeight || imageSource?.height || 0;
      dimensionsMatchCrop =
        cw > 0 &&
        ch > 0 &&
        iw > 0 &&
        ih > 0 &&
        Math.abs((iw - cw) / cw) < 0.05 &&
        Math.abs((ih - ch) / ch) < 0.05;
    }

    if (img.cropData) {
      const srcEqImageDataUrl = !!(
        img.imageDataUrl && img.src === img.imageDataUrl
      );
      const skipAlreadyCropped = srcEqImageDataUrl && dimensionsMatchCrop;
    }

    if (!hasCropData) return false;

    // STABILITY FIX: Rotated+cropped images should always apply crop when using the pinned pre-rotated source.
    if (hasStraightenedCrop && imageSource === rotatedOriginalImage) {
      return true;
    }

    // CRITICAL: When we're displaying the cropped image (src is imageDataUrl), skip applying crop
    // only when the displayed image dimensions actually match the crop (bitmap is already cropped).
    // After leave/revisit, imageDataUrl may point at the full image, so dimensionsMatchCrop is false —
    // we must apply crop in that case.
    if (
      img.imageDataUrl &&
      img.src === img.imageDataUrl &&
      img.cropData &&
      dimensionsMatchCrop
    ) {
      return false; // Bitmap is already cropped; dimensions match
    }

    // CRITICAL FIX: For top-layer images using originalImage, always apply crop
    // This handles the first crop operation where img.image hasn't been updated yet
    if (img.isOnTopLayer && img.cropData && imageSource === img.originalImage) {
      return true;
    }

    // CRITICAL FIX: For top-layer images using rotatedOriginalImage, always apply crop
    if (
      img.isOnTopLayer &&
      img.cropData &&
      imageSource === rotatedOriginalImage
    ) {
      return true;
    }

    // CRITICAL FIX: For ANY image using originalImage (not just top-layer), apply crop
    // This handles the case where img.image === img.originalImage (crop not applied yet)
    if (img.cropData && imageSource === img.originalImage) {
      return true;
    }

    // CRITICAL FIX: For ANY image using rotatedOriginalImage, apply crop
    if (img.cropData && imageSource === rotatedOriginalImage) {
      return true;
    }

    // CRITICAL FIX: If we're using img.image (the cropped result) for a cropped image without baked transforms,
    // DON'T apply crop again only when img.image is actually the cropped result (dimensions match crop).
    // When dimensionsMatchCrop is false (e.g. after leave/revisit with full image as data URL), we must apply crop.
    if (
      img.cropData &&
      !img.hasBakedTransforms &&
      imageSource === img.image &&
      !isApiImage &&
      img.image !== img.originalImage &&
      dimensionsMatchCrop
    ) {
      return false;
    }

    // For API images using rotatedOriginalImage, apply crop (coordinates are in rotated space)
    if (isApiImage && imageSource === rotatedOriginalImage) {
      return true;
    }

    // For local blobs with baked transforms, don't apply crop (already cropped)
    if (hasComplexTransforms && !isApiImage) {
      return false;
    }

    // For other cases, apply crop
    if (img.cropData) return true;
  }, [
    cropProperties,
    hasStraightenedCrop,
    isApiImage,
    imageSource,
    img.image,
    img.imageDataUrl,
    img.originalImage,
    cropDataSignature,
    img.hasBakedTransforms,
    img.isOnTopLayer,
    img.src,
    rotatedOriginalImage,
    hasComplexTransforms,
  ]);

  const imageSrc = React.useMemo(() => {
    // If we have crop data AND (transforms are NOT baked OR it's an API image), use the original uncropped image source
    // For API images with baked transforms, we still need original because S3 URL contains original
    if (
      img.cropData &&
      img.originalImageDataUrl &&
      (!img.hasBakedTransforms || isApiImage)
    ) {
      return img.originalImageDataUrl;
    }
    // Otherwise use the current image source
    return img.src;
  }, [
    img.cropData,
    img.src,
    img.originalImageDataUrl,
    img.hasBakedTransforms,
    isApiImage,
  ]);

  // DOWNSCALING DISABLED: No runtime downscaling so advanced crop modal is not blurry
  const [runtimeDownscaledImage, setRuntimeDownscaledImage] = useState(null);
  const lastDownscaledImageSourceRef = useRef(null);
  // useEffect(() => { setRuntimeDownscaledImage(null); lastDownscaledImageSourceRef.current = null; }, [img.src, img.id]);

  // CRITICAL FIX: Force re-render when image is replaced (crop or replace)
  // This ensures the new image displays immediately, including on second/subsequent crops
  const lastImageSrcRef = useRef(img.src);
  useEffect(() => {
    const node = imageRef.current;
    if (!node || !img.image) return;
    // Run when _replacedAt is set (replace/crop flow) OR when node is showing a different image
    // (e.g. crop updated img.image but Konva node didn't refresh)
    const nodeCurrentImage = node.image();
    const shouldForceUpdate =
      !!img._replacedAt || bitmapDiffersFromNode(nodeCurrentImage, img.image);
    if (!shouldForceUpdate) return;

    lastImageSrcRef.current = img.src;

    const newImage = img.image;
    const deepClear = !!img._replacedAt;

    // CRITICAL FIX: Only update if the image is fully loaded
    if (!newImage.complete || newImage.naturalWidth === 0) {
      newImage.onload = () => {
        node.clearCache();
        node.image(newImage);
        const layer = node.getLayer();
        if (layer) {
          if (deepClear) layer.clearBeforeDraw(true);
          layer.batchDraw();
        }
        const stage = node.getStage();
        if (stage) {
          stage.batchDraw();
        }
      };
      return;
    }

    node.clearCache();
    node.image(newImage);

    const layer = node.getLayer();
    if (layer) {
      if (deepClear) layer.clearBeforeDraw(true);
      layer.batchDraw();
    }
    const stage = node.getStage();
    if (stage) {
      stage.batchDraw();
    }
  }, [img._replacedAt, img.image, img.id, img.src]);

  // DOWNSCALING DISABLED: Runtime downscaling effect commented out so advanced crop modal is not blurry
  // useEffect(() => { ... if (maxScale > 2) downscaleImage(); }, [imageSource, ...]);

  // Use full-resolution imageSource (no runtime downscaling)
  const finalImageSource = imageSource;

  // CRITICAL FIX: Memoize transformer to prevent multiple instances on re-renders
  // This must be called BEFORE any early returns (React hooks rules)
  const transformerElement = React.useMemo(() => {
    const shouldShowTransformer =
      imageIsSelected &&
      ((isOffCanvas && img.isOnTopLayer) || (!isOffCanvas && !img.isOnTopLayer)) &&
      !isDuplicatePlacementActive;

    if (DEBUG_IMAGE_TRANSFORMER_FLOW && imageIsSelected) {
    }

    if (!shouldShowTransformer) return null;

    return (
      <Transformer
        key={`image-transformer-${img.id}-${img.maintainAspectRatio || false}-${
          img.maintainAspectRatio
            ? "bottom-right"
            : "middle-right-bottom-center-bottom-right"
        }-${img.maintainAspectRatio ? "locked" : "free"}`}
        ref={(node) => {
          const useLocalTransformer =
            (isOffCanvas && img.isOnTopLayer) ||
            (!isOffCanvas && !img.isOnTopLayer);
          imageTransformerRef.current = useLocalTransformer ? node : null;
        }}
        nodes={imageRef.current ? [imageRef.current] : []}
        padding={0}
        rotateEnabled={!isDuplicatePlacementActive}
        keepRatio={img.maintainAspectRatio || false}
        enabledAnchors={(() => {
          if (isDuplicatePlacementActive) {
            return [];
          }
          // CRITICAL: Keep anchors visible during drag
          const anchors =
            img.maintainAspectRatio === true
              ? ["bottom-right"]
              : ["middle-right", "bottom-center", "bottom-right"];
          return anchors; // Always show anchors, even during drag
        })()}
        resizeEnabled={true}
        borderEnabled={true}
        flipEnabled={false}
        centeredScaling={false}
        onTransformStart={(e) => {
          crossStageGestureBegin?.(
            typeof imageRef.current?.getStage === "function"
              ? imageRef.current.getStage()
              : null,
            e?.evt,
          );
          // PERFORMANCE: Clear cache when transform starts for accurate resize/rotate
          const node = imageRef.current;
          if (node?.clearCache) {
            node.clearCache();
            node.getLayer()?.batchDraw();
          }
          // Store top-left corner for ALL images so onTransformEnd/useLayoutEffect can use it (prevents position drift).
          if (node) {
            const useTopLeft = img.useTopLeftAnchor === true;
            const w = node.width() * (node.scaleX() || 1);
            const h = node.height() * (node.scaleY() || 1);
            transformStartPositionRef.current = {
              topLeftX: useTopLeft ? node.x() : node.x() - w / 2,
              topLeftY: useTopLeft ? node.y() : node.y() - h / 2,
              useTopLeftAnchor: useTopLeft,
              startRotation: normalizeRotationDeg(node.rotation()),
            };
          } else {
            transformStartPositionRef.current = null;
          }
          if (onTransformStart) {
            onTransformStart();
          }
        }}
        boundBoxFunc={(oldBox, newBox) => {
          // Apply minimum size constraints
          const minWidth = Math.max(10, oldBox.width * 0.1);
          const minHeight = Math.max(10, oldBox.height * 0.1);
          const clamped = { ...newBox };
          if (clamped.width < minWidth) clamped.width = minWidth;
          if (clamped.height < minHeight) clamped.height = minHeight;

          // Keep box origin fixed only during resize, not rotation.
          // Locking x/y while rotater is active causes aggressive rotation jumps
          // for main-canvas non-top-layer images.
          const activeAnchor =
            typeof imageTransformerRef.current?.getActiveAnchor === "function"
              ? imageTransformerRef.current.getActiveAnchor()
              : null;
          const isResize = !!activeAnchor && activeAnchor !== "rotater";
          if (isResize) {
            clamped.x = oldBox.x;
            clamped.y = oldBox.y;
          }

          // Allow free transform beyond stage bounds; clamp on transform-end only.
          return clamped;
        }}
        anchorSize={Math.max(8, 8 / Math.max(0.3, effectiveZoomLevel || 1))}
        anchorStroke="#007bff"
        anchorFill="#ffffff"
        anchorStrokeWidth={Math.max(
          2,
          2 / Math.max(0.3, effectiveZoomLevel || 1),
        )}
        rotateAnchorOffset={30}
        onTransformEnd={() => {
          crossStageGestureEnd?.();
        }}
      />
    );
  }, [
    imageIsSelected,
    img.isOnTopLayer,
    isOffCanvas,
    isDuplicatePlacementActive,
    img.id,
    img.maintainAspectRatio,
    img.useTopLeftAnchor,
    effectiveZoomLevel,
    isDragging, // Recompute anchors when drag state changes
    crossStageGestureBegin,
    crossStageGestureEnd,
  ]);

  // CRITICAL FIX: Validate image source before rendering
  // Don't render the Konva Image if the image source is invalid
  // This check must come AFTER all hooks (React hooks rules)
  if (!finalImageSource || !(finalImageSource instanceof window.Image)) {
    return null;
  }

  // When containerRotationZero: we must use the pre-rotated source so crop shows straightened content
  // without rotating the box. Don't render until rotatedOriginalImage is ready (avoids wrong crop + no rotation).
  const needsStraightenedCrop =
    containerRotationZero &&
    img.cropData &&
    img.originalCropRotation !== undefined &&
    img.originalCropRotation !== 0 &&
    img.src &&
    (String(img.src).startsWith("http://") ||
      String(img.src).startsWith("https://"));
  // First-paint stability: for straighten+crop payloads, do not paint intermediate
  // (unstraightened) frames. Wait until pre-rotated source is prepared, then render once.
  const shouldBlockUntilPreRotatedReady =
    hasStraightenedCrop &&
    !rotatedOriginalImage &&
    (String(img.src || "").startsWith("http://") ||
      String(img.src || "").startsWith("https://"));
  if (shouldBlockUntilPreRotatedReady) {
    return null;
  }

  // Crop is computed in original-image space (e.g. 800x800). When we display a different-sized
  // image (e.g. downscaled 440x457), Konva expects crop in that image's space — so scale crop.
  const cropToApply = (() => {
    if (
      !shouldApplyCrop ||
      !cropProperties ||
      Object.keys(cropProperties).length === 0
    ) {
      return {};
    }
    const srcW = finalImageSource?.naturalWidth;
    const srcH = finalImageSource?.naturalHeight;
    // When drawing the pre-rotated image, crop is in its space; use its dimensions as basis so we don't scale wrong.
    const useRotatedBasis =
      imageSource === rotatedOriginalImage &&
      rotatedOriginalImage &&
      srcW &&
      srcH;
    const basisW = useRotatedBasis
      ? srcW
      : (img.originalImage?.naturalWidth ??
        img.image?.naturalWidth ??
        img.originalWidth);
    const basisH = useRotatedBasis
      ? srcH
      : (img.originalImage?.naturalHeight ??
        img.image?.naturalHeight ??
        img.originalHeight);
    if (!basisW || !basisH || !srcW || !srcH) {
      return cropProperties;
    }
    if (srcW === basisW && srcH === basisH) {
      // Clamp to source bounds to prevent out-of-bounds draw (artifact/little image from bottom, white area)
      const cx = Math.max(0, Math.min(cropProperties.cropX, srcW - 1));
      const cy = Math.max(0, Math.min(cropProperties.cropY, srcH - 1));
      const clamped = {
        cropX: cx,
        cropY: cy,
        cropWidth: Math.max(1, Math.min(cropProperties.cropWidth, srcW - cx)),
        cropHeight: Math.max(1, Math.min(cropProperties.cropHeight, srcH - cy)),
      };
      if (img.cropData) return clamped;
    }
    const rawX = Math.round((cropProperties.cropX * srcW) / basisW);
    const rawY = Math.round((cropProperties.cropY * srcH) / basisH);
    const rawW = Math.max(
      1,
      Math.ceil((cropProperties.cropWidth * srcW) / basisW),
    );
    const rawH = Math.max(
      1,
      Math.ceil((cropProperties.cropHeight * srcH) / basisH),
    );
    const cropX = Math.max(0, Math.min(rawX, srcW - 1));
    const cropY = Math.max(0, Math.min(rawY, srcH - 1));
    const applied = {
      cropX,
      cropY,
      cropWidth: Math.max(1, Math.min(rawW, srcW - cropX)),
      cropHeight: Math.max(1, Math.min(rawH, srcH - cropY)),
    };

    return applied;
  })();

  // CRITICAL FIX: Only apply flip transforms if they're NOT already baked into the pixels
  // When hasBakedTransforms is true WITH local blobs, transforms are baked into blob pixels
  // For API images (S3 URLs), transforms are NEVER baked - S3 URL always contains original image
  // IMPORTANT: API images ALWAYS need flip via scale, even if hasBakedTransforms is true
  // (hasBakedTransforms=true on API image is a database inconsistency - ignore it)
  const shouldApplyFlipViaScale = isApiImage || !img.hasBakedTransforms;

  const calculatedScaleX =
    img.flipHorizontal && shouldApplyFlipViaScale
      ? -Math.abs(img.scaleX || 1)
      : Math.abs(img.scaleX || 1);
  const calculatedScaleY =
    img.flipVertical && shouldApplyFlipViaScale
      ? -Math.abs(img.scaleY || 1)
      : Math.abs(img.scaleY || 1);

  const isApiImageRotation =
    img.src &&
    (img.src.startsWith("http://") || img.src.startsWith("https://"));
  const usingPreRotatedSource = imageSource === rotatedOriginalImage;
  const straightenRotation = img.originalCropRotation || 0;
  const postCropRotation = img.rotation || 0;
  let computedRotation = 0;

  if (containerRotationZero && usingPreRotatedSource) {
    computedRotation = 0;
  } else if (containerRotationZero && !usingPreRotatedSource) {
    computedRotation = (straightenRotation + postCropRotation) % 360;
  } else if (usingPreRotatedSource) {
    // rotatedOriginalImage already has straighten rotation applied.
    computedRotation = postCropRotation % 360;
  } else if (img.hasBakedTransforms && !isApiImageRotation) {
    computedRotation = postCropRotation % 360;
  } else {
    computedRotation = (straightenRotation + postCropRotation) % 360;
  }

  const finalRotation =
    computedRotation < 0 ? computedRotation + 360 : computedRotation;
  if (imageRef.current) {
    imageRef.current._hasRotation =
      finalRotation !== 0 && finalRotation !== 360;
    imageRef.current._finalRotation = finalRotation;
  }

  // Debug only: trace signature changes for rotated+cropped images, especially on unselect.
  if (DEBUG_ROTATED_CROP_UNSELECT && hasStraightenedCrop) {
    const sourceKind =
      imageSource === rotatedOriginalImage
        ? "rotatedOriginalImage"
        : imageSource === img.originalImage
          ? "originalImage"
          : imageSource === img.image
            ? "image"
            : imageSource === syncedImageFromSrc
              ? "syncedImageFromSrc"
              : "other";
    const cropSig = cropToApply
      ? `${cropToApply.cropX ?? "na"},${cropToApply.cropY ?? "na"},${cropToApply.cropWidth ?? "na"},${cropToApply.cropHeight ?? "na"}`
      : "no-crop";
    const sig = {
      id: img.id,
      selected: !!imageIsSelected,
      isOnTopLayer: !!img.isOnTopLayer,
      sourceKind,
      shouldApplyCrop: !!shouldApplyCrop,
      cropSig,
      rotation: finalRotation,
      postRotation: img.rotation || 0,
      straighten: img.originalCropRotation || 0,
      x: img.x,
      y: img.y,
    };

    const prev = debugPrevSignatureRef.current;
    const changed =
      !prev ||
      prev.selected !== sig.selected ||
      prev.sourceKind !== sig.sourceKind ||
      prev.shouldApplyCrop !== sig.shouldApplyCrop ||
      prev.cropSig !== sig.cropSig ||
      prev.rotation !== sig.rotation ||
      prev.x !== sig.x ||
      prev.y !== sig.y;

    if (changed) {
      const phase = prev?.selected && !sig.selected ? "UNSELECT" : "UPDATE";
    }

    debugPrevSignatureRef.current = sig;
  }

  const effectiveImageOpacity =
    img.opacity !== undefined && img.opacity !== null ? img.opacity : 1;
  // Full-opacity duplicate behind the main Image composites incorrectly when the front node
  // is semi-transparent (selection dimming / user opacity): alpha blends with the copy below.
  const occlusionBlockerOpacity = effectiveImageOpacity < 1 ? 0 : 1;

  const syncOcclusionWithImageNode = (imageNode) => {
    if (!renderOcclusionBlocker) return;
    const blockerNode = occlusionRef.current;
    if (!blockerNode || !imageNode) return;

    blockerNode.x(imageNode.x());
    blockerNode.y(imageNode.y());
    blockerNode.rotation(imageNode.rotation());
    blockerNode.scaleX(imageNode.scaleX());
    blockerNode.scaleY(imageNode.scaleY());
    blockerNode.width(imageNode.width());
    blockerNode.height(imageNode.height());
    blockerNode.offsetX(imageNode.offsetX());
    blockerNode.offsetY(imageNode.offsetY());
    blockerNode.opacity(occlusionBlockerOpacity);
    blockerNode.getLayer()?.batchDraw();
  };

  return (
    <>
      {renderOcclusionBlocker && (
        <KonvaImage
          id={`${img.id}_occlusion_blocker`}
          x={img.x}
          y={img.y}
          width={img.width}
          height={img.height}
          rotation={finalRotation}
          {...(Object.keys(cropToApply).length > 0 ? cropToApply : {})}
          offsetX={img.useTopLeftAnchor === true ? 0 : (img.width || 0) / 2}
          offsetY={img.useTopLeftAnchor === true ? 0 : (img.height || 0) / 2}
          scaleX={calculatedScaleX}
          scaleY={calculatedScaleY}
          image={finalImageSource}
          opacity={occlusionBlockerOpacity}
          listening={false}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
          ref={occlusionRef}
        />
      )}
      <KonvaImage
        key={img.id}
        src={imageSrc}
        name="KonvaImage"
        imageName={img.name}
        imageNumber={img.imageNumber}
        image={finalImageSource}
        x={img.x}
        y={img.y}
        id={img.id}
        width={img.width}
        height={img.height}
        rotation={finalRotation}
        {...(Object.keys(cropToApply).length > 0 ? cropToApply : {})}
        offsetX={(() => {
          const offset =
            img.useTopLeftAnchor === true ? 0 : (img.width || 0) / 2;
          return offset;
        })()}
        offsetY={(() => {
          const offset =
            img.useTopLeftAnchor === true ? 0 : (img.height || 0) / 2;
          return offset;
        })()}
        // CRITICAL FIX: Remove fill and stroke for rotated images to prevent bounding box hit detection
        // For images, Konva uses the actual image pixels for hit detection by default
        // Adding fill="transparent" causes the entire bounding box to be clickable
        // By removing fill/stroke, only the actual image pixels are clickable
        fill={undefined}
        stroke={undefined}
        strokeWidth={0}
        hitFunc={(context, shape) => {
          // Get the actual image dimensions
          const width = shape.width();
          const height = shape.height();

          // Draw the actual image rectangle for hit detection
          context.beginPath();
          context.rect(0, 0, width, height);
          context.closePath();
          context.fillStrokeShape(shape);
        }}
        ref={(node) => {
          imageRef.current = node;
          if (img.cropData && node) {
            const applied = Object.keys(cropToApply).length > 0;
          }

          // CRITICAL: During active drag, skip any node updates so drag is not interrupted
          if (node && isDraggingRef.current) {
            return;
          }

          // CRITICAL FIX: Force Konva to update the image when bitmap actually changes.
          // Do not treat new Image() with same .src as a change (avoids flicker while drawing other elements).
          const shouldForceImage =
            node &&
            finalImageSource &&
            (!!img._replacedAt ||
              bitmapDiffersFromNode(node.image(), finalImageSource));
          if (shouldForceImage) {
            const deepClear = !!img._replacedAt;
            node.clearCache();
            node.image(finalImageSource);
            const layer = node.getLayer();
            if (layer) {
              if (deepClear) layer.clearBeforeDraw(true);
              layer.batchDraw();
            }
            const stage = node.getStage();
            if (stage) {
              stage.batchDraw();
            }
          }

          // CRITICAL FIX: If drag just finished, prevent React-Konva from applying stale position props
          if (
            node &&
            justFinishedDragRef.current &&
            dragEndPositionRef.current
          ) {
            // CRITICAL FIX: For top-layer images, check if node position is more recent
            if (img.isOnTopLayer) {
              const nodeX = node.x();
              const nodeY = node.y();
              const dragEndX = dragEndPositionRef.current.x;
              const dragEndY = dragEndPositionRef.current.y;
              const positionDiff =
                Math.abs(nodeX - dragEndX) + Math.abs(nodeY - dragEndY);
              const propsMatchNode =
                Math.abs(img.x - nodeX) < 0.1 && Math.abs(img.y - nodeY) < 0.1;

              if (positionDiff > 1 || propsMatchNode) {
                // Clear the flags since this is from group drag or state has been synced
                justFinishedDragRef.current = false;
                dragEndPositionRef.current = null;
                lastKonvaPositionRef.current = { x: nodeX, y: nodeY };
                return; // Don't override position
              }
            }

            // Force the node to the drag end position immediately
            const expectedX = dragEndPositionRef.current.x;
            const expectedY = dragEndPositionRef.current.y;
            node.x(expectedX);
            node.y(expectedY);
            node.getLayer()?.batchDraw();
          }

          // During transform, React-Konva can re-apply stale props; override from stored top-left (all images).
          const r = transformStartPositionRef.current;
          if (node && r?.useTopLeftAnchor) {
            const w = node.width() * (node.scaleX() || 1);
            const h = node.height() * (node.scaleY() || 1);
            node.x(r.topLeftX);
            node.y(r.topLeftY);
            node.getLayer()?.batchDraw();
          }

          // Simplified position fixing for top-layer images
          const nodeIsDragging = node?.getAttr?.("_isDragging") === true;
          const isInGroupDrag = isGroupDraggingRef?.current === true;
          if (
            node &&
            img.isOnTopLayer &&
            !isDragging &&
            !nodeIsDragging &&
            !justFinishedDragRef.current &&
            !justFinishedTransformRef.current &&
            !isTransforming &&
            !isInGroupDrag
          ) {
            // CRITICAL FIX: For top-layer images, use simpler position management
            // Only fix position if there's a significant difference and we're not in a transition state
            // CRITICAL: Don't fix position during group drag - handleGroupDragMove is managing it
            // CRITICAL FIX: Don't fix position after rotation - the node position is correct
            const nodeX = node.x();
            const nodeY = node.y();
            const propsX = img.x;
            const propsY = img.y;
            const positionDiff =
              Math.abs(nodeX - propsX) + Math.abs(nodeY - propsY);

            // CRITICAL FIX: Only fix position if there's a significant difference (> 5px) to avoid micro-adjustments
            // Increased threshold from 2px to 5px to prevent position corrections after rotation
            // When an image is rotated, small position differences are expected due to rounding
            if (positionDiff > 5) {
              node.x(propsX);
              node.y(propsY);
              node.getLayer()?.batchDraw();
              lastKonvaPositionRef.current = { x: propsX, y: propsY };
            }
          }

          // CRITICAL: For top layer images with useTopLeftAnchor, ensure offsets are 0.
          // CRITICAL: Skip during group drag – handleGroupDragMove is managing the node
          // position imperatively. Resetting offsets mid-drag shifts the visual anchor and
          // causes the image (and the whole group) to jump downward on every render.
          const isInGroupDragForAnchor = isGroupDraggingRef?.current === true;
          if (
            img.isOnTopLayer &&
            node &&
            img.useTopLeftAnchor === true &&
            !isTransforming &&
            !justFinishedTransformRef.current &&
            !isInGroupDragForAnchor
          ) {
            const currentOffsetX = node.offsetX();
            const currentOffsetY = node.offsetY();

            if (currentOffsetX !== 0 || currentOffsetY !== 0) {
              node.offsetX(0);
              node.offsetY(0);
              node.x(img.x);
              node.y(img.y);
              node.getLayer()?.batchDraw();
            }
          }
        }}
        // CRITICAL: Set scale AFTER crop properties to ensure flips aren't overridden
        scaleX={calculatedScaleX}
        scaleY={calculatedScaleY}
        opacity={effectiveImageOpacity}
        dragDistance={10}
        draggable={(() => {
          // CRITICAL: Keep draggable stable during active drag to prevent interruption
          // If currently dragging, keep draggable=true regardless of prop changes
          if (isDraggingRef.current) {
            return true;
          }
          return isDraggable && !isTransforming;
        })()}
        listening={(() => {
          // DEBUG: Log listening state for rotated images
          const rotation = imageRef.current?._finalRotation || 0;
          // CRITICAL FIX: Images should always listen for clicks (to allow selection)
          // Only disable listening during transforms to prevent interference
          const shouldListen = !isTransforming;

          if (rotation !== 0) {
          }

          return shouldListen;
        })()}
        perfectDrawEnabled={false} // PERFORMANCE FIX: Disable pixel-perfect rendering for better performance
        shadowForStrokeEnabled={!isDragging}
        onDragStart={(e) => {
          // In multi-select mode with a single selected image, prefer click-toggle behavior
          // over accidental tiny drags that immediately reselect on drag-end.
          if (isMultiSelectMode && imageIsSelected && selectedObjectsCount <= 1) {
            e?.target?.stopDrag?.();
            return;
          }
          // CRITICAL SAFETY CHECK: Verify mouse button is actually pressed
          if (e?.evt?.buttons !== undefined && e.evt.buttons === 0) {
            const node = e.target;
            if (node) {
              node.stopDrag();
            }
            return;
          }

          setIsDragging(true);
          isDraggingRef.current = true; // CRITICAL: Set ref to keep draggable stable during drag

          // CRITICAL FIX: Store original position when drag starts for top-layer images
          // This ensures we can use the original position even if props change during drag
          if (img.isOnTopLayer && !isOffCanvas && !isPreview) {
            nodePositionRef.current = { x: img.x, y: img.y };
          }

          // DEBUG: Log drag start for rotated images
          const rotation = imageRef.current?._finalRotation || 0;
          if (rotation !== 0) {
          }

          // CRITICAL FIX: Mark this image node as actively dragging so transformer
          // logic (in both Canvas and ImageElement) can hide anchors while dragging.
          if (e?.evt && e.target) {
            e.target.setAttr("_isDragging", true);
          }

          // PERFORMANCE: Clear cache when dragging starts for smooth visual updates
          const node = imageRef.current;
          if (node?.clearCache) {
            node.clearCache();
            node.getLayer()?.batchDraw();
          }

          // Call the onDragStart handler if provided (for group drag initialization)
          onDragStart?.();
          crossStageGestureBegin?.(e?.target?.getStage?.() ?? null, e.evt);
          syncOcclusionWithImageNode(e?.target || imageRef.current);
        }}
        dragBoundFunc={
          constrainToStage
            ? (pos) => {
                // CRITICAL FIX: For top-layer images, check if mouse is over off-canvas and lock position
                // dragBoundFunc is called BEFORE onDragMove, so we need to check mouse position here
                if (
                  img.isOnTopLayer &&
                  !isOffCanvas &&
                  !isPreview &&
                  nodePositionRef.current
                ) {
                  const node = imageRef.current;
                  const stage = node?.getStage();
                  if (stage) {
                    try {
                      // Get mouse position in stage coordinates
                      const pointerPos = stage.getPointerPosition();
                      if (pointerPos) {
                        // Get stage container bounding box to convert to client coordinates
                        const stageContainer = stage.container();
                        if (stageContainer) {
                          const stageBBox =
                            stageContainer.getBoundingClientRect();
                          const clientX = stageBBox.left + pointerPos.x;
                          const clientY = stageBBox.top + pointerPos.y;

                          // Check if mouse is over off-canvas area
                          const offCanvasContainer =
                            document.querySelector("[data-off-canvas-stage]") ||
                            document.querySelector(
                              ".off-canvas-stage-container",
                            ) ||
                            document.querySelector('[class*="off-canvas"]');

                          if (offCanvasContainer) {
                            const offBBox =
                              offCanvasContainer.getBoundingClientRect();
                            const isOverOffCanvas =
                              clientX >= offBBox.left &&
                              clientX <= offBBox.right &&
                              clientY >= offBBox.top &&
                              clientY <= offBBox.bottom;

                            // Don't lock position when dragging to off-canvas - allow it to change
                            // The original image is hidden via opacity: 0, so it can move freely
                            // The preview will show at the correct position
                          }
                        }
                      }
                    } catch (e) {
                      // If we can't get pointer position, fall through to normal behavior
                    }
                  }
                }

                // Allow freer movement during drag; final clamping is handled on drag-end.
                const node = imageRef.current;
                const stage = node?.getStage();
                if (!node || !stage) return pos;

                const stageWidth = stage.width();
                const stageHeight = stage.height();

                const currentRect = node.getClientRect();
                const insetX = getEffectiveInset(
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                  stageWidth,
                  currentRect.width,
                );
                const insetY = getEffectiveInset(
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                  stageHeight,
                  currentRect.height,
                );
                const offsetX = currentRect.x - node.x();
                const offsetY = currentRect.y - node.y();
                const minX = insetX - offsetX;
                const minY = insetY - offsetY;
                const maxX = stageWidth - insetX - currentRect.width - offsetX;
                const maxY = stageHeight - insetY - currentRect.height - offsetY;
                const clampedX = Math.max(minX, Math.min(maxX, pos.x));
                const clampedY = Math.max(minY, Math.min(maxY, pos.y));

                if (isOffCanvas) {
                  // Off-canvas image drag: allow crossing seam/bounds while dragging.
                  return { x: pos.x, y: pos.y };
                }

                // Main-canvas image drag: do not hard-limit at stage edge during drag move.
                // Clamp/normalize on drag-end so final persisted position stays valid.
                return { x: pos.x, y: pos.y };
              }
            : undefined
        }
        onClick={(e) => {
          if (handledToggleOnMouseDownRef.current) {
            handledToggleOnMouseDownRef.current = false;
            return;
          }
          if (!isPrimaryMouseButton(e.evt)) {
            return;
          }
          // CRITICAL FIX: For rotated images, verify the click is actually on the image
          // Konva's onClick fires for the entire bounding box, but we only want clicks on the actual image
          const node = imageRef.current;
          if (node && img.isOnTopLayer) {
            const stage = node.getStage();
            if (stage) {
              const pointerPos = stage.getPointerPosition();
              if (pointerPos) {
                // Use getIntersection to check if the click is actually on this image
                const clickedNode = stage.getIntersection(pointerPos);
                const isActuallyOnImage =
                  clickedNode && clickedNode.id() === img.id;
                const clickedNodeType = clickedNode?.getClassName?.();
                const isTransformerNode =
                  clickedNodeType === "Transformer" ||
                  clickedNodeType === "Circle" ||
                  clickedNodeType === "Rect" ||
                  clickedNode?.getParent?.()?.getClassName?.() ===
                    "Transformer";

                // If user is clicking transformer anchors/border, never treat as false image click.
                // Otherwise selection gets cleared and anchors appear broken.
                if (isTransformerNode) {
                  return;
                }

                if (!isActuallyOnImage) {
                  // Click is not actually on this image - manually clear selection

                  // Directly call the onClick prop (imageRenderer's onClick) with a modified event
                  // that simulates a stage background click to trigger deselection
                  if (typeof onClick === "function") {
                    // Create a synthetic event that looks like a stage click
                    const syntheticStageEvent = {
                      ...e,
                      target: stage,
                      currentTarget: stage,
                      _isFalseImageClick: true, // Mark this as a false click for debugging
                    };
                    onClick(syntheticStageEvent);
                  }

                  // Don't call handleImageClick - we've triggered deselection via onClick
                  return;
                }
              }
            }
          }

          // CRITICAL: In clone mode, don't select images when clicking on them
          if (typeof onClick === "function") {
            onClick(e);
          }

          // Only call handleImageClick if onClick didn't already consume the event.
          // Konva handlers may mark prevention on e.evt and/or cancelBubble without setting e.defaultPrevented.
          const isClickAlreadyHandled =
            e.defaultPrevented ||
            e.cancelBubble === true ||
            e?.evt?.defaultPrevented === true ||
            e?.evt?.cancelBubble === true;
          if (
            typeof handleImageClick === "function" &&
            !isClickAlreadyHandled
          ) {
            handleImageClick(img.id, e);
          }
        }}
        onMouseDown={(e) => {
          if (
            isPrimaryMouseButton(e.evt) &&
            isMultiSelectMode &&
            imageIsSelected &&
            selectedObjectsCount <= 1
          ) {
            handledToggleOnMouseDownRef.current = true;
            e?.evt?.preventDefault?.();
            e?.evt?.stopPropagation?.();
            e.cancelBubble = true;
            if (typeof handleImageClick === "function") {
              handleImageClick(img.id, e);
            }
            return;
          }
          // CRITICAL: Track mousedown on this image to enable two-click selection behavior
          // This prevents the first click from immediately selecting the image when another element is selected
          // The first click will unselect the current element, the second click will select this image
          if (recentDraggableElementMousedownRef) {
            recentDraggableElementMousedownRef.current = {
              nodeId: img.id,
              timestamp: Date.now(),
            };
          }
        }}
        onContextMenu={(e) => {
          // Browser "Copy image" on the canvas copies the layer bitmap, not this Image's pixels — pastes often
          // show a blank/white box. When Clipboard API is available, replace clipboard with the real bitmap.
          if (isPreview) return;
          const node = imageRef.current;
          if (!node) return;
          const source = node.image();
          if (!source) return;
          const w = source.naturalWidth || source.width || 0;
          const h = source.naturalHeight || source.height || 0;
          if (w < 1 || h < 1) return;
          if (!navigator.clipboard?.write) return;

          let canvas;
          try {
            canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d", { alpha: true });
            ctx.drawImage(source, 0, 0);
          } catch {
            return;
          }

          e.evt.preventDefault();
          e.evt.stopPropagation();

          // Clipboard write must start in the same user gesture as preventDefault; pass a Promise into
          // ClipboardItem so write() is synchronous from this handler (toBlob alone would lose activation).
          const pngPromise = new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
              try {
                canvas.width = 0;
                canvas.height = 0;
              } catch {
                /* ignore */
              }
              if (blob) resolve(blob);
              else reject(new Error("image/png toBlob returned null"));
            }, "image/png");
          });

          navigator.clipboard
            .write([new ClipboardItem({ "image/png": pngPromise })])
            .catch((err) => {
              console.warn("Copy image to clipboard failed:", err);
            });
        }}
        onDragMove={(e) => {
          const node = e.target;

          // CRITICAL FIX: For top-layer images, detect if dragging to off-canvas and set ref
          // This ref is checked in dragBoundFunc to lock position
          if (
            img.isOnTopLayer &&
            !isOffCanvas &&
            !isPreview &&
            nodePositionRef.current &&
            isDragging
          ) {
            const evt = e.evt;
            if (evt) {
              const clientX = evt.clientX;
              const clientY = evt.clientY;

              // Check if mouse is over off-canvas area
              // Try to find off-canvas stage container
              const offCanvasContainer =
                document.querySelector("[data-off-canvas-stage]") ||
                document.querySelector(".off-canvas-stage-container") ||
                document.querySelector('[class*="off-canvas"]');

              if (offCanvasContainer) {
                const offBBox = offCanvasContainer.getBoundingClientRect();
                const isOverOffCanvas =
                  clientX >= offBBox.left &&
                  clientX <= offBBox.right &&
                  clientY >= offBBox.top &&
                  clientY <= offBBox.bottom;

                // Set ref so dragBoundFunc can use it
                isDraggingToOffCanvasRef.current = isOverOffCanvas;

                if (isOverOffCanvas) {
                  // Mouse is over off-canvas - allow node to move freely (it's hidden via opacity: 0)
                  // Calculate current mouse position and pass to onDragMove for preview
                  const stage = node.getStage();
                  if (stage) {
                    const stageBBox = stage.container().getBoundingClientRect();
                    // Get mouse position relative to main canvas stage (where the node is)
                    const mainCanvasRelativeX = clientX - stageBBox.left;
                    const mainCanvasRelativeY = clientY - stageBBox.top;

                    // Pass current mouse position to onDragMove so preview stays where mouse is
                    // The imageRenderer's handleDragPreview will convert this to off-canvas coordinates
                    onDragMove?.(
                      {
                        id: img.id,
                        x: mainCanvasRelativeX, // Current mouse position (in main canvas coords)
                        y: mainCanvasRelativeY,
                        width: img.width,
                        height: img.height,
                        rotation: (() => {
                          let rotation = node.rotation();
                          rotation = rotation % 360;
                          if (rotation < 0) rotation += 360;
                          return rotation;
                        })(),
                        scaleX: node.scaleX(),
                        scaleY: node.scaleY(),
                        type: "Image",
                        compress: img.compress,
                        name: img.name,
                        maintainAspectRatio: img.maintainAspectRatio,
                        flipHorizontal: img.flipHorizontal,
                        flipVertical: img.flipVertical,
                        hasBakedTransforms: img.hasBakedTransforms,
                        isOnTopLayer: img.isOnTopLayer,
                      },
                      e,
                    );
                    // Keep occlusion blocker perfectly aligned while dragging toward off-canvas
                    syncOcclusionWithImageNode(node);
                    return; // Early return when dragging to off-canvas
                  }
                } else {
                  // Mouse is over main canvas - allow normal drag
                  isDraggingToOffCanvasRef.current = false;
                }
              } else {
                // Can't find off-canvas container - assume we're on main canvas
                isDraggingToOffCanvasRef.current = false;
              }
            }
          } else {
            // Not dragging or not top-layer - clear flag
            isDraggingToOffCanvasRef.current = false;
          }

          // CRITICAL: Normal drag behavior - allow node to move
          // Simply call the onDragMove prop passed from imageRenderer
          // The imageRenderer handles all group drag logic and coordinate conversion
          let rotation = node.rotation();
          rotation = rotation % 360;
          if (rotation < 0) {
            rotation += 360;
          }

          onDragMove?.(
            {
              id: img.id,
              x: node.x(),
              y: node.y(),
              width: img.width,
              height: img.height,
              rotation: rotation,
              scaleX: node.scaleX(),
              scaleY: node.scaleY(),
              type: "Image",
              compress: img.compress,
              name: img.name,
              maintainAspectRatio: img.maintainAspectRatio,
              flipHorizontal: img.flipHorizontal,
              flipVertical: img.flipVertical,
              hasBakedTransforms: img.hasBakedTransforms,
              isOnTopLayer: img.isOnTopLayer,
            },
            e,
          );
          // Keep occlusion blocker in sync with the live Konva node during normal drag
          syncOcclusionWithImageNode(node);
        }}
        onDragEnd={(e) => {
          crossStageGestureEnd?.();
          const node = e.target;
          // Mouse coordinates (needed for reliable cross-canvas drop detection)
          const mouseClientX =
            e.evt?.clientX ?? e.clientX ?? e.evt?.changedTouches?.[0]?.clientX;
          const mouseClientY =
            e.evt?.clientY ?? e.clientY ?? e.evt?.changedTouches?.[0]?.clientY;

          setIsDragging(false); // Re-enable high-quality rendering
          isDraggingRef.current = false; // CRITICAL: Clear ref to allow draggable prop to update

          // CRITICAL FIX: Clear the dragging flag for all images
          if (e.target) {
            e.target.setAttr("_isDragging", false);
          }
          // Top-layer bookkeeping remains specific to top-layer images
          if (img.isOnTopLayer) {
            // Clear the stored original position when drag ends
            nodePositionRef.current = null;
            // Clear the dragging to off-canvas flag
            isDraggingToOffCanvasRef.current = false;
          }

          // CRITICAL FIX: Read position directly from node like RectangleElement does
          // Don't modify the node position here - let handleElementDragEnd handle constraints
          // This matches RectangleElement's simpler approach that works correctly
          const draggedX = node.x();
          const draggedY = node.y();

          const sourceStage = node.getStage();
          // CRITICAL: Pass the node position directly to onDragEnd
          // handleElementDragEnd will handle constraints and coordinate conversion
          onDragEnd(
            img.id,
            draggedX,
            draggedY,
            sourceStage,
            mouseClientX,
            mouseClientY,
          );
          syncOcclusionWithImageNode(node);
        }}
        onTransformStart={() => {
          // CRITICAL: Store top-left corner for ALL images so resize keeps it fixed (works for center and top-left anchor).
          const node = imageRef.current;
          if (!node) {
            transformStartPositionRef.current = null;
            return;
          }
          const useTopLeft = img.useTopLeftAnchor === true;
          const w = node.width() * (node.scaleX() || 1);
          const h = node.height() * (node.scaleY() || 1);
          const topLeftX = useTopLeft ? node.x() : node.x() - w / 2;
          const topLeftY = useTopLeft ? node.y() : node.y() - h / 2;
          transformStartPositionRef.current = {
            topLeftX,
            topLeftY,
            useTopLeftAnchor: useTopLeft,
          };
          if (
            isMainNonTopTransformDebugEnabled &&
            !isOffCanvas &&
            !img.isOnTopLayer
          ) {
            console.log("[ImageElement main-non-top] transformStart", {
              id: img.id,
              x: node.x(),
              y: node.y(),
              width: node.width(),
              height: node.height(),
              rotation: node.rotation(),
              scaleX: node.scaleX(),
              scaleY: node.scaleY(),
            });
          }
        }}
        onTransform={(e) => {
          const node = imageRef.current;
          if (!node) return;
          const activeAnchor =
            e?.target?.getLayer?.()?.findOne?.("Transformer")?.getActiveAnchor?.() ||
            null;
          const isMainNonTopRotationInteraction =
            !isOffCanvas && !img.isOnTopLayer && activeAnchor === "rotater";

          // Keep top-left corner fixed for ALL images during resize
          const ref = transformStartPositionRef.current;
          if (ref?.useTopLeftAnchor) {
            const w = node.width() * (node.scaleX() || 1);
            const h = node.height() * (node.scaleY() || 1);
            node.x(ref.topLeftX);
            node.y(ref.topLeftY);
          }

          // CRITICAL FIX: Store current position BEFORE any scale changes
          // This prevents position drift during rapid transform operations
          const currentX = node.x();
          const currentY = node.y();
          const currentWidth = node.width();
          const currentHeight = node.height();
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          // Konva keeps base width/height and applies resize via scale — editor must see visual size.
          const displayWidth = currentWidth * Math.abs(scaleX || 1);
          const displayHeight = currentHeight * Math.abs(scaleY || 1);

          // Get current scale and rotation values
          // CRITICAL FIX: Always use the node's rotation during transform to match visual rotation
          // This ensures the rotation follows the cursor smoothly, just like rectangles
          let newRotation = node.rotation() % 360;
          if (newRotation < 0) newRotation += 360;

          // Pass the corrected scale values
          // CRITICAL: Use node position directly like rectangles do - don't adjust during transform
          // The transformer handles rotation correctly, we just need to pass through the values
          if (!isMainNonTopRotationInteraction) {
            onTransform?.({
              id: img.id,
              x: currentX,
              y: currentY,
              width: displayWidth,
              height: displayHeight,
              rotation: newRotation, // Always use newRotation to match visual rotation
              scaleX: scaleX,
              scaleY: scaleY,
              type: "Image",
              maintainAspectRatio: img.maintainAspectRatio,
              compress: img.compress,
              name: img.name,
              // Preserve crop-related properties
              cropData: img.cropData,
              originalImage: img.originalImage,
              originalImageDataUrl: img.originalImageDataUrl,
              originalCropRotation: img.originalCropRotation,
              originalWidth: img.originalWidth,
              originalHeight: img.originalHeight,
              originalAspectRatio: img.originalAspectRatio,
              // CRITICAL: Preserve flip properties during live transform
              flipHorizontal: img.flipHorizontal,
              flipVertical: img.flipVertical,
              hasBakedTransforms: img.hasBakedTransforms,
              // CRITICAL: Preserve isOnTopLayer to prevent checkbox fluctuation
              isOnTopLayer: img.isOnTopLayer,
            });
          }
          if (
            isMainNonTopTransformDebugEnabled &&
            !isOffCanvas &&
            !img.isOnTopLayer
          ) {
            console.log("[ImageElement main-non-top] transform", {
              id: img.id,
              activeAnchor,
              skippedLiveUpdate: isMainNonTopRotationInteraction,
              x: currentX,
              y: currentY,
              width: displayWidth,
              height: displayHeight,
              rotation: newRotation,
              scaleX,
              scaleY,
            });
          }
          syncOcclusionWithImageNode(node);
        }}
        onTransformEnd={(e) => {
          const node = imageRef.current;
          if (!node) return;

          // For main-canvas top-layer images, `Canvas.jsx` is responsible for
          // normalizing `scaleX/scaleY`, `width/height`, clamping, and persisting
          // them to React state via the top-layer `Transformer` handlers.
          // If we normalize inside `ImageElement` as well, the transformer end
          // logic can mis-detect whether a resize happened and then revert the
          // image back to its previous dimensions (making rotation/resize
          // appear "not working" for top-most stacked images).
          if (img.isOnTopLayer && !isOffCanvas) {
            justFinishedTransformRef.current = true;
            transformStartPositionRef.current = null;
            setTimeout(() => {
              justFinishedTransformRef.current = false;
            }, 100);
            syncOcclusionWithImageNode(node);
            return;
          }

          // Use stored top-left corner + new size so position never drifts (all images)
          const ref = transformStartPositionRef.current;
          const finalScaleX = node.scaleX();
          const finalScaleY = node.scaleY();

          // Normalize rotation to 0-360 range
          let totalRotation = node.rotation() % 360;
          if (totalRotation < 0) {
            totalRotation += 360;
          }

          // For straightened+cropped images rendered via pre-rotated source, node rotation
          // is already the post-crop rotation. For non-pre-rotated sources it is total
          // (straighten + post), so extract post to avoid auto-rotation on transform end.
          const straighten = img.originalCropRotation || 0;
          const isStraightenedCrop =
            !!img.cropData &&
            img.originalCropRotation !== undefined &&
            img.originalCropRotation !== 0;
          const usesPreRotatedSource =
            isStraightenedCrop && imageSource === rotatedOriginalImage;
          const isApiImageForRotation =
            img.src &&
            (img.src.startsWith("http://") || img.src.startsWith("https://"));

          let preservedRotation;
          if (usesPreRotatedSource || (img.hasBakedTransforms && !isApiImageForRotation)) {
            preservedRotation = totalRotation;
          } else {
            preservedRotation = (totalRotation - straighten) % 360;
            if (preservedRotation < 0) preservedRotation += 360;
          }

          // DEBUG: Log rotation calculation to track why rotation input isn't updating
          console.log("DEBUG: ImageElement rotation calculation in onTransformEnd", {
            nodeRotation: node.rotation(),
            totalRotation: totalRotation,
            straighten: straighten,
            usesPreRotatedSource: usesPreRotatedSource,
            hasBakedTransforms: img.hasBakedTransforms,
            isApiImageForRotation: isApiImageForRotation,
            preservedRotation: preservedRotation,
            originalImageRotation: img.rotation,
            nodeRotationIsNaN: isNaN(node.rotation()),
            preservedRotationIsNaN: isNaN(preservedRotation)
          });

          // CRITICAL FIX: Always use node dimensions to prevent resize reversion
          // Calculate dimensions from the actual node state, not from potentially stale props
          // This prevents resize changes from reverting during subsequent rotations
          const newWidth = Math.max(5, Math.abs(node.width() * finalScaleX));
          const newHeight = Math.max(5, Math.abs(node.height() * finalScaleY));

          // If maintain aspect ratio is enabled, ensure proportional scaling
          let finalWidth = newWidth;
          let finalHeight = newHeight;
          if (img.maintainAspectRatio) {
            const aspectRatio =
              img.originalAspectRatio || node.width() / node.height();
            const dominantScale = Math.max(
              Math.abs(finalScaleX),
              Math.abs(finalScaleY),
            );

            finalWidth = Math.max(5, node.width() * dominantScale);
            finalHeight = Math.max(5, finalWidth / aspectRatio);

            // Round to avoid floating-point precision issues
            finalWidth = Math.round(finalWidth * 100) / 100;
            finalHeight = Math.round(finalHeight * 100) / 100;
          }

          // Clamp transform-end dimensions to what can fit inside stage bounds
          // with the transformer buffer, then clamp/reposition x/y.
          const previousRotation = normalizeRotationDeg(
            ref?.startRotation ?? img.rotation ?? preservedRotation,
          );
          const didRotateInThisTransform =
            Math.abs(normalizeRotationDeg(totalRotation) - previousRotation) > 0.01;
          const stage = node.getStage();
          const stageWidth = stage?.width?.() || window.innerWidth;
          const stageHeight = stage?.height?.() || window.innerHeight;
          let maxWidthByBuffer = Math.max(
            5,
            stageWidth - TRANSFORMER_ANCHOR_BUFFER_PX * 2,
          );
          const maxHeightByBuffer = Math.max(
            5,
            stageHeight - TRANSFORMER_ANCHOR_BUFFER_PX * 2,
          );

          if (isOffCanvas) {
            // Off-canvas has a central main-column overlay; image must fit in one side strip.
            const { mainZoneStart, mainZoneEnd } =
              getOffCanvasMainColumnBounds(stageWidth);
            const leftStripWidth = Math.max(0, mainZoneStart);
            const rightStripWidth = Math.max(0, stageWidth - mainZoneEnd);
            const hasRightStrip = rightStripWidth > 1e-3;
            const seamMidX = (mainZoneStart + mainZoneEnd) / 2;
            const currentNodeX = Number(node.x?.()) || 0;
            const anchorX =
              img.useTopLeftAnchor === true
                ? currentNodeX + finalWidth / 2
                : currentNodeX;
            const useRightStrip = hasRightStrip && anchorX >= seamMidX;
            const stripWidth = useRightStrip ? rightStripWidth : leftStripWidth;
            maxWidthByBuffer = Math.max(
              5,
              stripWidth - TRANSFORMER_ANCHOR_BUFFER_PX * 2,
            );
          }

          if (!didRotateInThisTransform) {
            const theta = (normalizeRotationDeg(totalRotation) * Math.PI) / 180;
            const absCos = Math.abs(Math.cos(theta));
            const absSin = Math.abs(Math.sin(theta));
            const availableProjectedW = Math.max(5, maxWidthByBuffer);
            const availableProjectedH = Math.max(5, maxHeightByBuffer);
            const EPS = 1e-6;
            if (img.maintainAspectRatio) {
              const projectedW =
                Math.max(1, finalWidth) * absCos + Math.max(0, finalHeight) * absSin;
              const projectedH =
                Math.max(1, finalWidth) * absSin + Math.max(0, finalHeight) * absCos;
              const fitScale = Math.min(
                1,
                availableProjectedW / Math.max(1, projectedW),
                availableProjectedH / Math.max(1, projectedH),
              );
              finalWidth = Math.max(5, finalWidth * fitScale);
              finalHeight = Math.max(5, finalHeight * fitScale);
            } else {
              const clampToRotationAwareFit = (requestedW, requestedH) => {
                let w = Math.max(5, Number(requestedW) || 5);
                let h = Math.max(5, Number(requestedH) || 5);

                const maxWFromHoriz =
                  absCos > EPS
                    ? (availableProjectedW - h * absSin) / absCos
                    : Number.POSITIVE_INFINITY;
                const maxWFromVert =
                  absSin > EPS
                    ? (availableProjectedH - h * absCos) / absSin
                    : Number.POSITIVE_INFINITY;
                const maxW = Math.max(5, Math.min(maxWFromHoriz, maxWFromVert));
                w = Math.max(5, Math.min(w, maxW));

                const maxHFromHoriz =
                  absSin > EPS
                    ? (availableProjectedW - w * absCos) / absSin
                    : Number.POSITIVE_INFINITY;
                const maxHFromVert =
                  absCos > EPS
                    ? (availableProjectedH - w * absSin) / absCos
                    : Number.POSITIVE_INFINITY;
                const maxH = Math.max(5, Math.min(maxHFromHoriz, maxHFromVert));
                h = Math.max(5, Math.min(h, maxH));

                return { width: w, height: h };
              };
              const fitted = clampToRotationAwareFit(finalWidth, finalHeight);
              finalWidth = fitted.width;
              finalHeight = fitted.height;
            }
          }

          // Use RectangleElement's simple approach: set node properties first, then use current position
          // This avoids complex rotation math and lets Konva handle positioning correctly
          
          const useTopLeftAnchor = img.useTopLeftAnchor === true;
          const isEffectivelyRotated =
            Math.abs(Math.sin((normalizeRotationDeg(totalRotation) * Math.PI) / 180)) >
            1e-3;

          // CRITICAL FIX: Reset scale based on hasBakedTransforms
          const isApiImage =
            img.src &&
            (img.src.startsWith("http://") || img.src.startsWith("https://"));
          const shouldApplyFlipViaScale = !img.hasBakedTransforms || isApiImage;

          let resetScaleX, resetScaleY;
          if (shouldApplyFlipViaScale) {
            resetScaleX = img.flipHorizontal ? -1 : 1;
            resetScaleY = img.flipVertical ? -1 : 1;
          } else {
            resetScaleX = 1;
            resetScaleY = 1;
          }

          // Use RectangleElement's approach: set node properties first, then use current position
          node.scaleX(resetScaleX);
          node.scaleY(resetScaleY);
          node.width(finalWidth);
          node.height(finalHeight);
          if (img.useTopLeftAnchor === true) {
            node.offsetX(0);
            node.offsetY(0);
          } else {
            // Keep center-anchor math in sync with normalized dimensions before clamping.
            // Otherwise getClientRect can still use stale offsets from pre-transform size.
            node.offsetX(finalWidth / 2);
            node.offsetY(finalHeight / 2);
          }
          
          // Ensure Konva recomputes geometry before we read position
          node.getLayer?.()?.batchDraw?.();
          
          // Use current node position (like RectangleElement) - no complex math needed
          const positionX = node.x();
          const positionY = node.y();
          
          console.log("DEBUG: ImageElement using current node position after property update", {
            positionAfterUpdate: { x: positionX, y: positionY },
            finalDimensions: { width: finalWidth, height: finalHeight }
          });

          // Use RectangleElement's simple clamping approach
          let clampedX = positionX;
          let clampedY = positionY;
          
          // Intentionally skip early generic clamping here.
          // A single branch-specific clamp below is more stable and avoids
          // conflicting x corrections.

          // Apply final position (RectangleElement approach)
          node.x(clampedX);
          node.y(clampedY);
          // Rotation policy (main stage): keep requested rotation if x-only push-in
          // makes it fit; otherwise revert to previous rotation (no size shrinking).
          if (!isOffCanvas && didRotateInThisTransform) {
            node.rotation(normalizeRotationDeg(totalRotation));
            const shiftedAtRequested = clampNodePositionInsideStageByClientRect(
              node,
              stageWidth,
              stageHeight,
              TRANSFORMER_ANCHOR_BUFFER_PX,
              { clampX: true, clampY: true },
            );
            const fitsAtRequested = isClientRectInsideStage(
              node.getClientRect({ relativeTo: stage }),
              stageWidth,
              stageHeight,
              TRANSFORMER_ANCHOR_BUFFER_PX,
            );
            if (fitsAtRequested) {
              clampedX = shiftedAtRequested.x;
              clampedY = shiftedAtRequested.y;
              preservedRotation = normalizeRotationDeg(totalRotation);
            } else {
              node.rotation(previousRotation);
              const shiftedAtPrevious = clampNodePositionInsideStageByClientRect(
                node,
                stageWidth,
                stageHeight,
                TRANSFORMER_ANCHOR_BUFFER_PX,
                { clampX: true, clampY: true },
              );
              clampedX = shiftedAtPrevious.x;
              clampedY = shiftedAtPrevious.y;
              preservedRotation = previousRotation;
            }
          } else if (!isOffCanvas && !didRotateInThisTransform && isEffectivelyRotated) {
            // Resize-only on rotated main-canvas images:
            // preserve gesture Y and only push X back inside stage.
            const shifted = clampNodePositionInsideStageByClientRect(
              node,
              stageWidth,
              stageHeight,
              TRANSFORMER_ANCHOR_BUFFER_PX,
              { clampX: true, clampY: false },
            );
            clampedX = shifted.x;
            clampedY = shifted.y;
          } else if (isOffCanvas || didRotateInThisTransform || isEffectivelyRotated) {
            // For off-canvas and rotated transforms, apply strip-aware clamp.
            if (isOffCanvas) {
              const isCurrentClientRectInsideOffCanvasBounds = () => {
                const rect = node.getClientRect({ relativeTo: stage });
                const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
                const insetX = TRANSFORMER_ANCHOR_BUFFER_PX;
                const insetY = TRANSFORMER_ANCHOR_BUFFER_PX;

                const isInsideLeftStrip =
                  rect.x >= insetX && rect.x + rect.width <= mainZoneStart - insetX;
                const isInsideRightStrip =
                  rect.x >= mainZoneEnd + insetX &&
                  rect.x + rect.width <= stageWidth - insetX;
                const isInsideX = isInsideLeftStrip || isInsideRightStrip;
                const isInsideY =
                  rect.y >= insetY && rect.y + rect.height <= stageHeight - insetY;
                return isInsideX && isInsideY;
              };

              if (didRotateInThisTransform) {
                // Match main-canvas behavior: keep requested rotation if it can fit
                // in an off-canvas strip after clamp, otherwise revert to previous.
                node.rotation(normalizeRotationDeg(totalRotation));
                const shiftedAtRequested = clampNodeInsideOffCanvasStripsByClientRect(
                  node,
                  stageWidth,
                  stageHeight,
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                  { clampX: true, clampY: true },
                );
                if (isCurrentClientRectInsideOffCanvasBounds()) {
                  clampedX = shiftedAtRequested.x;
                  clampedY = shiftedAtRequested.y;
                  preservedRotation = normalizeRotationDeg(totalRotation);
                } else {
                  node.rotation(previousRotation);
                  const shiftedAtPrevious = clampNodeInsideOffCanvasStripsByClientRect(
                    node,
                    stageWidth,
                    stageHeight,
                    TRANSFORMER_ANCHOR_BUFFER_PX,
                    { clampX: true, clampY: true },
                  );
                  clampedX = shiftedAtPrevious.x;
                  clampedY = shiftedAtPrevious.y;
                  preservedRotation = previousRotation;
                }
              } else {
                const offCanvasClampOptions =
                  !didRotateInThisTransform && isEffectivelyRotated
                    ? { clampX: true, clampY: false }
                    : { clampX: true, clampY: true };
                const shifted = clampNodeInsideOffCanvasStripsByClientRect(
                  node,
                  stageWidth,
                  stageHeight,
                  TRANSFORMER_ANCHOR_BUFFER_PX,
                  offCanvasClampOptions,
                );
                clampedX = shifted.x;
                clampedY = shifted.y;
              }
            } else {
              // For rotated non-off-canvas transforms, apply clamping as before
              const shifted = clampNodePositionInsideStageByClientRect(
                node,
                stageWidth,
                stageHeight,
                TRANSFORMER_ANCHOR_BUFFER_PX,
              );
              clampedX = shifted.x;
              clampedY = shifted.y;
            }
          } else {
            // Main-canvas non-rotated transforms: always apply client-rect clamp.
            const shifted = clampNodePositionInsideStageByClientRect(
              node,
              stageWidth,
              stageHeight,
              TRANSFORMER_ANCHOR_BUFFER_PX,
              { clampX: true, clampY: true },
            );
            clampedX = shifted.x;
            clampedY = shifted.y;
          }

          if (!isOffCanvas && !didRotateInThisTransform && !isEffectivelyRotated) {
          }

          // Send updated properties
          const updatedAttrs = {
            ...img,
            id: img.id,
            x: clampedX,
            y: clampedY,
            rotation: preservedRotation,
            scaleX: resetScaleX,
            scaleY: resetScaleY,
            width: finalWidth,
            height: finalHeight,
            isTransform: true,
            originalAspectRatio: img.originalAspectRatio,
            cropData: img.cropData,
            originalImage: img.originalImage,
            originalImageDataUrl: img.originalImageDataUrl,
            originalCropRotation: img.originalCropRotation,
            originalWidth: img.originalWidth,
            originalHeight: img.originalHeight,
            flipHorizontal: img.flipHorizontal,
            flipVertical: img.flipVertical,
            hasBakedTransforms: img.hasBakedTransforms,
            // CRITICAL: Preserve isOnTopLayer to prevent checkbox fluctuation
            isOnTopLayer: img.isOnTopLayer,
          };
          if (
            isMainNonTopTransformDebugEnabled &&
            !isOffCanvas &&
            !img.isOnTopLayer
          ) {
            console.log("[ImageElement main-non-top] transformEnd", {
              id: img.id,
              x: updatedAttrs.x,
              y: updatedAttrs.y,
              width: updatedAttrs.width,
              height: updatedAttrs.height,
              rotation: updatedAttrs.rotation,
              scaleX: updatedAttrs.scaleX,
              scaleY: updatedAttrs.scaleY,
            });
          }

          onTransformEnd(updatedAttrs);

          // Clear so useLayoutEffect stops overriding position after transform ends
          transformStartPositionRef.current = null;

          // CRITICAL FIX: Set flag to prevent React from overriding node dimensions
          justFinishedTransformRef.current = true;

          // Clear the flag after state has had time to update
          setTimeout(() => {
            justFinishedTransformRef.current = false;
          }, 100);
        }}
      />

      {/* Image Transformer (for rotation and scaling) */}
      {/* CRITICAL FIX: 
          - Main canvas top-layer images: managed by Canvas.jsx via topLayerTransformerRef (no local transformer)
          - Off-canvas top-layer images: need local transformer (topLayerTransformerRef only works for main canvas)
          - All non-top-layer images: use local transformer
      */}
      {transformerElement}

      {/* Copy button for off-canvas images */}
      {(() => {
        // Copy button commented out
        const shouldShowCopyButton =
          false &&
          imageIsSelected &&
          !isPreview &&
          isOffCanvas &&
          typeof onCopyToOppositeCanvas === "function";

        if (!shouldShowCopyButton) return null;

        const imgX = img.x ?? 0;
        const imgY = img.y ?? 0;
        const imgWidth = img.width ?? 0;
        const imgHeight = img.height ?? 0;

        // Images are centered, so calculate top-left position
        const topLeftX = imgX - imgWidth / 2;
        const topLeftY = imgY - imgHeight / 2;

        const copyButtonLeft = Math.max(
          4,
          Math.min(topLeftX + imgWidth - 28, window.innerWidth - 28),
        );
        const copyButtonTop = Math.max(
          4,
          Math.min(topLeftY - 36, window.innerHeight - 28),
        );

        return (
          <Html>
            <div
              style={{
                position: "absolute",
                top: `${copyButtonTop}px`,
                left: `${copyButtonLeft}px`,
                pointerEvents: "none",
                zIndex: 30,
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCopyToOppositeCanvas?.();
                }}
                title="Copy to Main Canvas"
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "4px",
                  border: "1px solid #CBD5E1",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.18)",
                  cursor: "pointer",
                  pointerEvents: "auto",
                  padding: 0,
                  transition:
                    "background-color 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F1F5F9";
                  e.currentTarget.style.borderColor = "#94A3B8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#FFFFFF";
                  e.currentTarget.style.borderColor = "#CBD5E1";
                }}
              >
                <svg
                  width="16"
                  height="19"
                  viewBox="0 0 17 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.97572 15.75C5.47055 15.75 5.04297 15.575 4.69297 15.225C4.34297 14.875 4.16797 14.4474 4.16797 13.9423V2.55775C4.16797 2.05258 4.34297 1.625 4.69297 1.275C5.04297 0.925 5.47055 0.75 5.97572 0.75H14.3602C14.8654 0.75 15.293 0.925 15.643 1.275C15.993 1.625 16.168 2.05258 16.168 2.55775V13.9423C16.168 14.4474 15.993 14.875 15.643 15.225C15.293 15.575 14.8654 15.75 14.3602 15.75H5.97572ZM5.97572 14.25H14.3602C14.4372 14.25 14.5077 14.2179 14.5717 14.1538C14.6359 14.0898 14.668 14.0192 14.668 13.9423V2.55775C14.668 2.48075 14.6359 2.41025 14.5717 2.34625C14.5077 2.28208 14.4372 2.25 14.3602 2.25H5.97572C5.89872 2.25 5.82822 2.28208 5.76422 2.34625C5.70005 2.41025 5.66797 2.48075 5.66797 2.55775V13.9423C5.66797 14.0192 5.70005 14.0898 5.76422 14.1538C5.82822 14.2179 5.89872 14.25 5.97572 14.25ZM2.47572 19.25C1.97055 19.25 1.54297 19.075 1.19297 18.725C0.842969 18.375 0.667969 17.9474 0.667969 17.4423V4.55775H2.16797V17.4423C2.16797 17.5193 2.20005 17.5898 2.26422 17.6538C2.32822 17.7179 2.39872 17.75 2.47572 17.75H12.3602V19.25H2.47572Z"
                    fill="#1E293B"
                  />
                </svg>
              </button>
            </div>
          </Html>
        );
      })()}
    </>
  );
};

ImageElement.propTypes = {
  img: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    image: PropTypes.instanceOf(window.Image).isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    rotation: PropTypes.number,
    isSelected: PropTypes.bool,
    referenceUrl: PropTypes.string,
    compress: PropTypes.bool,
    maintainAspectRatio: PropTypes.bool,
    // Add scaleX and scaleY to propTypes if they are part of img object
    scaleX: PropTypes.number,
    scaleY: PropTypes.number,
    src: PropTypes.string, // Assuming src is also part of img
    name: PropTypes.string, // Assuming name is also part of img
    imageNumber: PropTypes.number, // Assuming imageNumber is also part of img
  }).isRequired,
  onClick: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func.isRequired,
  onDragMove: PropTypes.func, // Add onDragMove prop
  onTransformEnd: PropTypes.func,
  onTransform: PropTypes.func, // 👈 Add to propTypes
  setImages: PropTypes.func.isRequired,
  handleImageClick: PropTypes.func,
  constrainToStage: PropTypes.bool,
  recentDraggableElementMousedownRef: PropTypes.shape({
    current: PropTypes.object,
  }),
  isMultiSelectMode: PropTypes.bool,
  selectedObjectsCount: PropTypes.number,
  containerRotationZero: PropTypes.bool,
  renderOcclusionBlocker: PropTypes.bool,
  mainCanvasStageRef: PropTypes.shape({ current: PropTypes.any }),
  crossStageGestureBegin: PropTypes.func,
  crossStageGestureEnd: PropTypes.func,
};

ImageElement.defaultProps = {
  img: {
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
  },
  onClick: () => {}, // Provide default empty function for optional props
  onDragStart: () => {}, // Add default empty function for onDragStart
  onDragMove: () => {}, // Add default empty function for onDragMove
  onTransformEnd: () => {},
  onTransform: () => {}, // 👈 Add default empty function
  handleImageClick: () => {},
  constrainToStage: true,
  isMultiSelectMode: false,
  selectedObjectsCount: 0,
  renderOcclusionBlocker: false,
  mainCanvasStageRef: null,
  crossStageGestureBegin: null,
  crossStageGestureEnd: null,
};

export default ImageElement;
